/**
 * Elite Batch 12 Part 1 — Workflow Templates 1048–1067
 * Loyalty lifecycle, marketing attribution, marketing calendar, marketing mix,
 * spend tracking, Marketo campaigns, Meta ads, milestone celebrations,
 * consent remediation, and multi-creator pipelines.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 12 PART 1 — TEMPLATES 1048–1067
// =============================================================================

export const ELITE_BATCH12_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1048. LOYALTY POINTS EXPIRING (8 nodes)
  // ===================================================================

  "Loyalty Points Expiring": {
    summary:
      "Daily scan detects members with loyalty points nearing expiration, enriches member data from HubSpot, evaluates balance thresholds, generates AI redemption suggestions, sends urgency emails and SMS for high-value members via Twilio, logs engagement to Google Sheets, and routes delivery failures to Slack.",
    nodes: [
      trigger("Schedule: Daily loyalty points expiration scan", "schedule", {
        schedule: "0 7 * * * (Daily at 7 AM)",
        howItStarts:
          "A daily schedule scans the loyalty database for members whose points expire within 14 days, triggering personalized redemption outreach",
        setup:
          "Configure HubSpot API key with contacts read access and a custom property 'loyalty_points_balance' and 'points_expiry_date'. Provide Twilio account SID, auth token, and FROM number. Set up Google Sheets spreadsheet ID for engagement tracking and Slack webhook for failure alerts.",
        inputFields: [],
      }),

      // 2. Member enrichment from HubSpot
      hubspotGetNode(
        "2",
        "Enrich member profiles from HubSpot",
        280,
        'crm/v3/objects/contacts?properties=email,firstname,lastname,phone,loyalty_points_balance,points_expiry_date,total_revenue,hs_object_id&filterGroups=[{"filters":[{"propertyName":"points_expiry_date","operator":"LT","value":"{fourteen_days_from_now}"}]}]&limit=100',
        [
          {
            name: "results",
            customName: "expiring_members_json",
            type: "string",
          },
          {
            name: "total",
            customName: "expiring_member_count",
            type: "number",
          },
        ],
      ),

      // 3. Balance threshold condition
      n(
        "3",
        "condition",
        "Does member have high-value balance (>500 points)?",
        520,
        {
          conditionData: [
            {
              variable: "expiring_member_count",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 4. AI redemption suggestions
      aiNode(
        "4",
        "AI: Generate redemption suggestions",
        760,
        "You are a loyalty program specialist. Generate personalized redemption suggestions that maximize perceived value and create urgency. Return JSON with suggested redemption options tailored to the member profile.",
        "Members with expiring points data: {expiring_members_json}. For each member, suggest 3 compelling redemption options based on their balance. Include urgency language about expiry dates. Return redemption_email_subject, redemption_email_body (HTML with personalized suggestions and countdown), sms_message (under 160 chars, urgent tone with top redemption pick), high_value_count (members with balance > 500).",
        [
          {
            field: "redemption_email_subject",
            as: "redemption_subject",
            type: "string",
          },
          {
            field: "redemption_email_body",
            as: "redemption_body_html",
            type: "string",
          },
          { field: "sms_message", as: "redemption_sms", type: "string" },
          { field: "high_value_count", as: "high_value_count", type: "number" },
        ],
      ),

      // 5. Urgency email dispatch
      emailNode(
        "5",
        "Send urgency redemption email",
        1000,
        "{member_email}",
        "{redemption_subject}",
        "{redemption_body_html}",
      ),

      // 6. SMS for high-value members (Twilio)
      n("6", "sendSMS", "SMS alert for high-value members via Twilio", 1240, {
        sendSmsDetails: {
          to: "{member_phone}",
          message: "{redemption_sms}",
        },
      }),

      // 7. Engagement tracking (Sheets)
      sheetsNode(
        "7",
        "Log engagement to Google Sheets",
        1480,
        "LoyaltyExpiry",
        '[["{timestamp}","{expiring_member_count}","{high_value_count}","{redemption_subject}","{member_email}","email_sent","sms_sent"]]',
      ),

      // 8. Delivery failure handler (Slack)
      slackNode(
        "8",
        "Alert Slack on delivery failures",
        1720,
        ":warning: *Loyalty Points Expiry Alert*\\nExpiring members processed: {expiring_member_count}\\nHigh-value members: {high_value_count}\\nEmail delivery status: check logs\\nSMS delivery status: check logs\\nReview engagement sheet for details.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Has expiring members -> generate suggestions
      e("3", "8", "output_2"), // No expiring members -> notify Slack
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1049. LOYALTY RE-ENGAGEMENT FOR INACTIVE MEMBERS (9 nodes)
  // ===================================================================

  "Loyalty Re-Engagement for Inactive Members": {
    summary:
      "Weekly scan identifies inactive loyalty members, classifies inactivity severity in code, looks up member history from HubSpot, generates AI win-back content, calculates bonus point incentives, sends re-engagement emails and SMS nudges via Twilio, updates CRM status, and logs campaign analytics to Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly inactive member scan", "schedule", {
        schedule: "0 9 * * 1 (Monday at 9 AM)",
        howItStarts:
          "A weekly schedule scans loyalty program members for inactivity, identifying those with no transactions or engagement in the past 60+ days",
        setup:
          "Configure HubSpot API key with contacts read/write access and custom properties for loyalty_last_activity_date, loyalty_tier, and loyalty_points_balance. Provide Twilio credentials for SMS. Set up Google Sheets spreadsheet ID for analytics.",
        inputFields: [],
      }),

      // 2. Inactivity severity classification (runCode)
      n("2", "runCode", "Classify inactivity severity levels", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify members by inactivity duration
const now = Date.now();
const dayMs = 86400000;
// Simulated fetch — in production this reads from your loyalty DB or CRM export
const members = JSON.parse(vars.inactive_members_json || "[]");
const classified = members.map(m => {
  const lastActivity = new Date(m.loyalty_last_activity_date || 0).getTime();
  const daysSince = Math.floor((now - lastActivity) / dayMs);
  let severity = "low";
  if (daysSince > 180) severity = "critical";
  else if (daysSince > 90) severity = "high";
  else if (daysSince > 60) severity = "medium";
  return { ...m, days_inactive: daysSince, severity };
});
const critical = classified.filter(m => m.severity === "critical");
const high = classified.filter(m => m.severity === "high");
const medium = classified.filter(m => m.severity === "medium");
return {
  classified_members_json: JSON.stringify(classified),
  critical_count: critical.length,
  high_count: high.length,
  medium_count: medium.length,
  total_inactive: classified.length,
  severity_summary: critical.length + " critical, " + high.length + " high, " + medium.length + " medium"
};`,
          outputVariables: [
            { name: "classified_members_json", type: "string" },
            { name: "critical_count", type: "number" },
            { name: "high_count", type: "number" },
            { name: "medium_count", type: "number" },
            { name: "total_inactive", type: "number" },
            { name: "severity_summary", type: "string" },
          ],
        },
      }),

      // 3. Member history lookup (HubSpot)
      hubspotGetNode(
        "3",
        "Fetch member engagement history from HubSpot",
        520,
        'crm/v3/objects/contacts?properties=email,firstname,phone,loyalty_tier,loyalty_points_balance,loyalty_last_activity_date,total_revenue,num_associated_deals&filterGroups=[{"filters":[{"propertyName":"loyalty_last_activity_date","operator":"LT","value":"{sixty_days_ago}"}]}]&limit=100',
        [
          {
            name: "results",
            customName: "inactive_members_json",
            type: "string",
          },
          {
            name: "total",
            customName: "hubspot_inactive_count",
            type: "number",
          },
        ],
      ),

      // 4. AI win-back content
      aiNode(
        "4",
        "AI: Generate win-back content",
        760,
        "You are a loyalty marketing expert specializing in member re-engagement. Create compelling win-back messaging that acknowledges the member absence, highlights what they have missed, and offers a strong incentive to return. Tailor urgency based on inactivity severity. Return JSON.",
        "Inactive loyalty members classified by severity: {severity_summary}. Member data: {classified_members_json}. Generate winback_email_subject (personalized, creates curiosity), winback_email_body (HTML with severity-appropriate urgency, missed benefits recap, bonus point offer), sms_nudge (under 160 chars, friendly re-engagement with bonus teaser), recommended_bonus_multiplier (1.5x for medium, 2x for high, 3x for critical).",
        [
          {
            field: "winback_email_subject",
            as: "winback_subject",
            type: "string",
          },
          {
            field: "winback_email_body",
            as: "winback_body_html",
            type: "string",
          },
          { field: "sms_nudge", as: "winback_sms", type: "string" },
          {
            field: "recommended_bonus_multiplier",
            as: "bonus_multiplier",
            type: "string",
          },
        ],
      ),

      // 5. Bonus points calculator (runCode)
      n("5", "runCode", "Calculate bonus point incentives per member", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate personalized bonus point offers based on severity and tier
const members = JSON.parse(vars.classified_members_json || "[]");
const multiplier = parseFloat(vars.bonus_multiplier) || 2;
const bonusOffers = members.map(m => {
  const baseBonus = m.severity === "critical" ? 500 : m.severity === "high" ? 300 : 150;
  const tierMultiplier = m.loyalty_tier === "Gold" ? 1.5 : m.loyalty_tier === "Silver" ? 1.25 : 1;
  const totalBonus = Math.round(baseBonus * multiplier * tierMultiplier);
  return {
    email: m.email,
    name: m.firstname || "Member",
    phone: m.phone || "",
    severity: m.severity,
    current_points: m.loyalty_points_balance || 0,
    bonus_points: totalBonus,
    new_total: (m.loyalty_points_balance || 0) + totalBonus
  };
});
const totalBonusPoints = bonusOffers.reduce((sum, b) => sum + b.bonus_points, 0);
return {
  bonus_offers_json: JSON.stringify(bonusOffers),
  total_bonus_points_allocated: totalBonusPoints,
  avg_bonus: bonusOffers.length > 0 ? Math.round(totalBonusPoints / bonusOffers.length) : 0,
  offers_count: bonusOffers.length,
  sample_email: bonusOffers.length > 0 ? bonusOffers[0].email : "",
  sample_phone: bonusOffers.length > 0 ? bonusOffers[0].phone : ""
};`,
          outputVariables: [
            { name: "bonus_offers_json", type: "string" },
            { name: "total_bonus_points_allocated", type: "number" },
            { name: "avg_bonus", type: "number" },
            { name: "offers_count", type: "number" },
            { name: "sample_email", type: "string" },
            { name: "sample_phone", type: "string" },
          ],
        },
      }),

      // 6. Re-engagement email sequence
      emailNode(
        "6",
        "Send re-engagement email with bonus offer",
        1240,
        "{sample_email}",
        "{winback_subject}",
        "{winback_body_html}",
      ),

      // 7. SMS nudge (Twilio)
      smsNode(
        "7",
        "Send SMS nudge via Twilio",
        1480,
        "{sample_phone}",
        "{winback_sms}",
      ),

      // 8. CRM status update (HubSpot)
      hubspotPostNode(
        "8",
        "Update member re-engagement status in HubSpot",
        1720,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{contact_id}","properties":{"loyalty_reengagement_status":"outreach_sent","loyalty_reengagement_date":"{timestamp}","loyalty_bonus_offered":"{avg_bonus}"}}]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "updated_contact_id",
            type: "string",
          },
          {
            name: "status",
            customName: "hubspot_update_status",
            type: "string",
          },
        ],
      ),

      // 9. Campaign analytics (Sheets)
      sheetsNode(
        "9",
        "Log campaign analytics to Sheets",
        1960,
        "LoyaltyReEngagement",
        '[["{timestamp}","{total_inactive}","{severity_summary}","{offers_count}","{total_bonus_points_allocated}","{avg_bonus}","{winback_subject}","emails_sent","sms_sent"]]',
      ),
    ],
    edges: [
      e("1", "3"),
      e("3", "2"),
      e("2", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1050. LOYALTY SURVEY OR PREFERENCE REFRESH (8 nodes)
  // ===================================================================

  "Loyalty Survey or Preference Refresh": {
    summary:
      "Scheduled survey triggers member segmentation via code, uses AI to personalize survey content, sends invitation emails, listens for response webhooks, updates preference profiles in HubSpot, credits bonus points in code, and follows up with non-responders after a delay.",
    nodes: [
      trigger("Schedule: Quarterly preference survey", "schedule", {
        schedule: "0 10 1 */3 * (First day of every quarter at 10 AM)",
        howItStarts:
          "A quarterly schedule triggers a preference survey campaign, selecting eligible loyalty members for outreach to refresh their preferences and engagement profiles",
        setup:
          "Configure HubSpot API key with contacts read/write access. Set up survey tool (Outgrow Forms or similar) webhook for response ingestion. Provide Google Sheets spreadsheet ID and Slack webhook for non-responder follow-up tracking.",
        inputFields: [],
      }),

      // 2. Member segmentation filter (runCode)
      n("2", "runCode", "Segment members eligible for survey", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter members eligible for quarterly preference survey
const allMembers = JSON.parse(vars.loyalty_members_json || "[]");
const now = Date.now();
const dayMs = 86400000;
const eligible = allMembers.filter(m => {
  const lastSurvey = m.last_survey_date ? new Date(m.last_survey_date).getTime() : 0;
  const daysSinceLastSurvey = Math.floor((now - lastSurvey) / dayMs);
  const isActive = m.loyalty_status === "active";
  return isActive && daysSinceLastSurvey > 80; // At least 80 days since last survey
});
const byTier = {
  gold: eligible.filter(m => m.loyalty_tier === "Gold").length,
  silver: eligible.filter(m => m.loyalty_tier === "Silver").length,
  bronze: eligible.filter(m => m.loyalty_tier === "Bronze").length,
  standard: eligible.filter(m => !["Gold","Silver","Bronze"].includes(m.loyalty_tier)).length
};
return {
  eligible_members_json: JSON.stringify(eligible),
  eligible_count: eligible.length,
  tier_breakdown: byTier.gold + " Gold, " + byTier.silver + " Silver, " + byTier.bronze + " Bronze, " + byTier.standard + " Standard",
  sample_email: eligible.length > 0 ? eligible[0].email : "",
  sample_name: eligible.length > 0 ? (eligible[0].firstname || "Member") : ""
};`,
          outputVariables: [
            { name: "eligible_members_json", type: "string" },
            { name: "eligible_count", type: "number" },
            { name: "tier_breakdown", type: "string" },
            { name: "sample_email", type: "string" },
            { name: "sample_name", type: "string" },
          ],
        },
      }),

      // 3. AI survey personalization
      aiNode(
        "3",
        "AI: Personalize survey invitation",
        520,
        "You are a customer experience specialist. Create personalized survey invitations that feel valuable to the member, not like a chore. Emphasize that their input shapes their rewards. Return JSON.",
        "Eligible loyalty members: {eligible_count} across tiers: {tier_breakdown}. Sample member name: {sample_name}. Generate survey_email_subject (personal, emphasizes their importance), survey_email_body (HTML with personalized greeting, explains how preferences improve their rewards, includes survey link placeholder {survey_url}, mentions bonus points for completion), survey_bonus_description (what they earn for completing).",
        [
          {
            field: "survey_email_subject",
            as: "survey_subject",
            type: "string",
          },
          {
            field: "survey_email_body",
            as: "survey_body_html",
            type: "string",
          },
          {
            field: "survey_bonus_description",
            as: "bonus_description",
            type: "string",
          },
        ],
      ),

      // 4. Survey invitation email
      emailNode(
        "4",
        "Send survey invitation email",
        760,
        "{sample_email}",
        "{survey_subject}",
        "{survey_body_html}",
      ),

      // 5. Response webhook listener (hitApi — Outgrow Forms response fetch)
      n("5", "hitApi", "Fetch survey responses from Outgrow Forms", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.outgrow-forms.com/forms/{outgrow-forms_form_id}/responses?since={survey_send_timestamp}&page_size=100",
          headers: [
            { key: "Authorization", value: "Bearer {outgrow-forms_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "items",
              customName: "survey_responses_json",
              type: "string",
            },
            {
              name: "total_items",
              customName: "response_count",
              type: "number",
            },
          ],
        },
      }),

      // 6. Preference profile updater (HubSpot)
      hubspotPostNode(
        "6",
        "Update preference profiles in HubSpot",
        1240,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{contact_id}","properties":{"survey_completed_date":"{timestamp}","preference_communication_channel":"{preferred_channel}","preference_product_categories":"{preferred_categories}","last_survey_date":"{timestamp}"}}]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "updated_contact_id",
            type: "string",
          },
          {
            name: "status",
            customName: "preference_update_status",
            type: "string",
          },
        ],
      ),

      // 7. Bonus points credit (runCode)
      n("7", "runCode", "Credit bonus points for survey completion", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Credit bonus points to members who completed the survey
const responses = JSON.parse(vars.survey_responses_json || "[]");
const bonusPerSurvey = 100; // 100 points for completing survey
const credited = responses.map(r => ({
  email: r.hidden?.email || r.answers?.[0]?.email || "unknown",
  points_credited: bonusPerSurvey,
  response_id: r.response_id || r.token || "n/a",
  completed_at: r.submitted_at || new Date().toISOString()
}));
const totalPointsCredited = credited.length * bonusPerSurvey;
return {
  credited_members_json: JSON.stringify(credited),
  total_points_credited: totalPointsCredited,
  credited_count: credited.length,
  response_rate: vars.eligible_count > 0 ? ((credited.length / parseInt(vars.eligible_count)) * 100).toFixed(1) : "0",
  credit_summary: credited.length + " members credited " + totalPointsCredited + " total points"
};`,
          outputVariables: [
            { name: "credited_members_json", type: "string" },
            { name: "total_points_credited", type: "number" },
            { name: "credited_count", type: "number" },
            { name: "response_rate", type: "string" },
            { name: "credit_summary", type: "string" },
          ],
        },
      }),

      // 8. Non-responder follow-up (delay)
      n(
        "8",
        "delayAction",
        "Wait 5 days then follow up with non-responders",
        1720,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1051. MARKETING ATTRIBUTION REPORT (9 nodes)
  // ===================================================================

  "Marketing Attribution Report": {
    summary:
      "Weekly data collection pulls Google Ads and Meta Ads performance, matches CRM conversions from HubSpot, calculates multi-touch attribution in code, generates AI strategic insights, compiles reports to Google Sheets, emails executive summaries, and monitors data quality with a condition gate.",
    nodes: [
      trigger("Schedule: Weekly attribution report", "schedule", {
        schedule: "0 6 * * 1 (Monday at 6 AM)",
        howItStarts:
          "A weekly schedule triggers comprehensive marketing attribution analysis across all paid and organic channels, matching ad spend to CRM conversions",
        setup:
          "Configure Google Ads API credentials (customer ID, developer token, refresh token). Set Meta Marketing API access token and ad account ID. Provide HubSpot API key with deals and contacts read access. Set up Google Sheets spreadsheet ID for report output.",
        inputFields: [],
      }),

      // 2. Google Ads data pull
      n("2", "hitApi", "Pull Google Ads performance data", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, campaign.id, metrics.cost_micros, metrics.conversions, metrics.clicks, metrics.impressions, segments.date FROM campaign WHERE segments.date DURING LAST_7_DAYS",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "google_ads_data_json",
              type: "string",
            },
            {
              name: "results.length",
              customName: "google_ads_campaign_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Meta Ads data pull
      n(
        "3",
        "hitApi",
        "Pull Meta Ads performance data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.facebook.com/v19.0/act_{meta_ad_account_id}/insights?fields=campaign_name,campaign_id,spend,actions,impressions,clicks,cpc,cpm&date_preset=last_7d&level=campaign&limit=100",
            headers: [
              { key: "Authorization", value: "Bearer {meta_access_token}" },
            ],
            body: [],
            variables: [
              {
                name: "data",
                customName: "meta_ads_data_json",
                type: "string",
              },
              {
                name: "data.length",
                customName: "meta_ads_campaign_count",
                type: "number",
              },
            ],
          },
        },
        480,
      ),

      // 4. CRM conversion matcher (HubSpot)
      hubspotGetNode(
        "4",
        "Fetch CRM conversions from HubSpot",
        520,
        'crm/v3/objects/deals?properties=dealname,amount,closedate,dealstage,hs_analytics_source,hs_analytics_source_data_1,hs_analytics_source_data_2,createdate&filterGroups=[{"filters":[{"propertyName":"createdate","operator":"GTE","value":"{seven_days_ago_ms}"}]}]&limit=100',
        [
          { name: "results", customName: "crm_deals_json", type: "string" },
          { name: "total", customName: "total_new_deals", type: "number" },
        ],
      ),

      // 5. Multi-touch attribution calculator (runCode)
      n("5", "runCode", "Calculate multi-touch attribution model", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Multi-touch attribution: linear model across Google Ads, Meta Ads, and organic
const googleAds = JSON.parse(vars.google_ads_data_json || "[]");
const metaAds = JSON.parse(vars.meta_ads_data_json || "[]");
const deals = JSON.parse(vars.crm_deals_json || "[]");

// Calculate total spend by channel
const googleSpend = googleAds.reduce((s, c) => s + ((c.metrics?.cost_micros || 0) / 1000000), 0);
const metaSpend = metaAds.reduce((s, c) => s + parseFloat(c.spend || 0), 0);
const totalSpend = googleSpend + metaSpend;

// Match conversions to sources
const totalRevenue = deals.reduce((s, d) => s + parseFloat(d.properties?.amount || 0), 0);
const paidSearchDeals = deals.filter(d => (d.properties?.hs_analytics_source || "").includes("PAID_SEARCH"));
const paidSocialDeals = deals.filter(d => (d.properties?.hs_analytics_source || "").includes("PAID_SOCIAL"));
const organicDeals = deals.filter(d => (d.properties?.hs_analytics_source || "").includes("ORGANIC"));

const googleROAS = googleSpend > 0 ? (paidSearchDeals.reduce((s, d) => s + parseFloat(d.properties?.amount || 0), 0) / googleSpend).toFixed(2) : "0";
const metaROAS = metaSpend > 0 ? (paidSocialDeals.reduce((s, d) => s + parseFloat(d.properties?.amount || 0), 0) / metaSpend).toFixed(2) : "0";

return {
  google_spend: googleSpend.toFixed(2),
  meta_spend: metaSpend.toFixed(2),
  total_spend: totalSpend.toFixed(2),
  total_revenue: totalRevenue.toFixed(2),
  blended_roas: totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : "0",
  google_roas: googleROAS,
  meta_roas: metaROAS,
  paid_search_deals: paidSearchDeals.length,
  paid_social_deals: paidSocialDeals.length,
  organic_deals: organicDeals.length,
  total_deals: deals.length,
  attribution_summary: "Google ROAS: " + googleROAS + "x, Meta ROAS: " + metaROAS + "x, Blended: " + (totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : "0") + "x"
};`,
          outputVariables: [
            { name: "google_spend", type: "string" },
            { name: "meta_spend", type: "string" },
            { name: "total_spend", type: "string" },
            { name: "total_revenue", type: "string" },
            { name: "blended_roas", type: "string" },
            { name: "google_roas", type: "string" },
            { name: "meta_roas", type: "string" },
            { name: "paid_search_deals", type: "number" },
            { name: "paid_social_deals", type: "number" },
            { name: "organic_deals", type: "number" },
            { name: "total_deals", type: "number" },
            { name: "attribution_summary", type: "string" },
          ],
        },
      }),

      // 6. AI insight generator
      aiNode(
        "6",
        "AI: Generate strategic attribution insights",
        1000,
        "You are a marketing analytics strategist. Analyze attribution data across channels and provide actionable recommendations for budget allocation. Be specific about which channels to scale or reduce. Return JSON.",
        "Weekly attribution data — Total spend: ${total_spend}, Total revenue: ${total_revenue}. Google Ads: spend ${google_spend}, ROAS {google_roas}x, {paid_search_deals} deals. Meta Ads: spend ${meta_spend}, ROAS {meta_roas}x, {paid_social_deals} deals. Organic: {organic_deals} deals. Blended ROAS: {blended_roas}x. Return executive_summary (3-4 sentences), top_recommendation (single most impactful budget action), channel_grades (A-F for each channel), budget_reallocation_suggestion (specific % shifts), risk_flags (any concerning trends).",
        [
          {
            field: "executive_summary",
            as: "ai_executive_summary",
            type: "string",
          },
          {
            field: "top_recommendation",
            as: "ai_top_recommendation",
            type: "string",
          },
          { field: "channel_grades", as: "ai_channel_grades", type: "string" },
          {
            field: "budget_reallocation_suggestion",
            as: "ai_budget_suggestion",
            type: "string",
          },
          { field: "risk_flags", as: "ai_risk_flags", type: "string" },
        ],
      ),

      // 7. Report compilation (Sheets)
      sheetsNode(
        "7",
        "Compile attribution report to Sheets",
        1240,
        "AttributionReport",
        '[["{timestamp}","{total_spend}","{total_revenue}","{blended_roas}","{google_spend}","{google_roas}","{meta_spend}","{meta_roas}","{total_deals}","{paid_search_deals}","{paid_social_deals}","{organic_deals}","{ai_top_recommendation}"]]',
      ),

      // 8. Executive summary email
      emailNode(
        "8",
        "Send executive attribution summary",
        1480,
        "{marketing_lead_email}",
        "Weekly Attribution Report: {blended_roas}x Blended ROAS",
        "Marketing Attribution Report — Week of {report_date}\\n\\n{ai_executive_summary}\\n\\nChannel Performance:\\n- Google Ads: ${google_spend} spent, {google_roas}x ROAS, {paid_search_deals} deals\\n- Meta Ads: ${meta_spend} spent, {meta_roas}x ROAS, {paid_social_deals} deals\\n- Organic: {organic_deals} deals (no direct spend)\\n\\nBlended ROAS: {blended_roas}x on ${total_spend} total spend\\n\\nTop Recommendation: {ai_top_recommendation}\\n\\nBudget Suggestion: {ai_budget_suggestion}\\n\\nRisk Flags: {ai_risk_flags}\\n\\nFull report: {sheets_url}",
      ),

      // 9. Data quality monitor condition
      n(
        "9",
        "condition",
        "Is data quality sufficient for reliable attribution?",
        1720,
        {
          conditionData: [
            {
              variable: "total_deals",
              value: "5",
              comparison_operator: "GREATER THAN",
              logical_operator: "AND",
            },
            {
              variable: "total_spend",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1052. MARKETING CALENDAR TO TASK ASSIGNER (8 nodes)
  // ===================================================================

  "Marketing Calendar to Task Assigner": {
    summary:
      "Listens for marketing calendar events, extracts event details in code, checks for duplicate tasks via condition, selects appropriate task templates, creates Jira epics and tasks, auto-assigns team members in code, syncs to Notion dashboard, and sends Slack kick-off notifications.",
    nodes: [
      trigger("Webhook: Marketing calendar event created", "webhook_app", {
        source: "Google Calendar",
        event: "event.created",
        howItStarts:
          "A new event is added to the marketing calendar (e.g., campaign launch, content deadline, webinar), triggering automatic task creation and assignment",
        setup:
          "Configure Google Calendar webhook or Zapier integration to POST event data to your workflow URL. Provide Jira API token and project key. Set up Notion API key with database access. Configure Slack webhook for team notifications.",
        inputFields: [],
      }),

      // 2. Event detail extractor (runCode)
      n("2", "runCode", "Extract and classify event details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract marketing event details and classify task type
const payload = vars.webhook_payload || "{}";
const event = typeof payload === "string" ? JSON.parse(payload) : payload;
const summary = event.summary || event.title || "";
const description = event.description || "";
const startDate = event.start?.dateTime || event.start?.date || "";
const endDate = event.end?.dateTime || event.end?.date || "";
const attendees = (event.attendees || []).map(a => a.email).join(", ");

// Classify event type based on keywords
let eventType = "general";
const lowerSummary = summary.toLowerCase();
if (lowerSummary.includes("launch") || lowerSummary.includes("release")) eventType = "campaign_launch";
else if (lowerSummary.includes("webinar") || lowerSummary.includes("event")) eventType = "webinar";
else if (lowerSummary.includes("content") || lowerSummary.includes("blog")) eventType = "content";
else if (lowerSummary.includes("social") || lowerSummary.includes("post")) eventType = "social";
else if (lowerSummary.includes("email") || lowerSummary.includes("newsletter")) eventType = "email_campaign";

return {
  event_title: summary,
  event_description: description,
  event_start: startDate,
  event_end: endDate,
  event_attendees: attendees,
  event_type: eventType,
  event_id: event.id || event.iCalUID || "",
  days_until_event: startDate ? Math.ceil((new Date(startDate).getTime() - Date.now()) / 86400000) : 0
};`,
          outputVariables: [
            { name: "event_title", type: "string" },
            { name: "event_description", type: "string" },
            { name: "event_start", type: "string" },
            { name: "event_end", type: "string" },
            { name: "event_attendees", type: "string" },
            { name: "event_type", type: "string" },
            { name: "event_id", type: "string" },
            { name: "days_until_event", type: "number" },
          ],
        },
      }),

      // 3. Duplicate detection condition
      n(
        "3",
        "condition",
        "Is this a duplicate event (already has tasks)?",
        520,
        {
          conditionData: [
            {
              variable: "event_id",
              value: "",
              comparison_operator: "NOT EQUAL",
              logical_operator: "AND",
            },
            {
              variable: "days_until_event",
              value: "0",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 4. Task template selector (hitApi — Jira search for existing)
      n("4", "hitApi", "Search Jira for existing tasks for this event", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_base64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "jql",
              value:
                'project = {jira_project_key} AND summary ~ "{event_title}" AND created >= -7d',
            },
            { key: "maxResults", value: "5" },
          ],
          variables: [
            {
              name: "total",
              customName: "existing_task_count",
              type: "number",
            },
            {
              name: "issues",
              customName: "existing_issues_json",
              type: "string",
            },
          ],
        },
      }),

      // 5. Jira epic + task creator
      n("5", "hitApi", "Create Jira epic and subtasks", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_base64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"[{event_type}] {event_title}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"{event_description}"}]}]},"issuetype":{"name":"Epic"},"duedate":"{event_start}","labels":["marketing-calendar","auto-created","{event_type}"]}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_epic_id", type: "string" },
            { name: "key", customName: "jira_epic_key", type: "string" },
            { name: "self", customName: "jira_epic_url", type: "string" },
          ],
        },
      }),

      // 6. Team member auto-assignment (runCode)
      n("6", "runCode", "Auto-assign team members based on event type", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign team members based on event type
const eventType = vars.event_type || "general";
const assignments = {
  campaign_launch: { lead: "marketing-manager@company.com", support: ["designer@company.com", "copywriter@company.com", "analyst@company.com"] },
  webinar: { lead: "events-manager@company.com", support: ["content-lead@company.com", "designer@company.com"] },
  content: { lead: "content-lead@company.com", support: ["seo-specialist@company.com", "designer@company.com"] },
  social: { lead: "social-manager@company.com", support: ["designer@company.com", "copywriter@company.com"] },
  email_campaign: { lead: "email-manager@company.com", support: ["copywriter@company.com", "analyst@company.com"] },
  general: { lead: "marketing-manager@company.com", support: ["coordinator@company.com"] }
};
const team = assignments[eventType] || assignments.general;
const subtasks = [
  { title: "Creative brief and assets", assignee: team.support[0] || team.lead },
  { title: "Copy and messaging", assignee: team.support[1] || team.lead },
  { title: "Review and approval", assignee: team.lead },
  { title: "Launch execution", assignee: team.lead }
];
return {
  assigned_lead: team.lead,
  assigned_support: team.support.join(", "),
  subtasks_json: JSON.stringify(subtasks),
  subtask_count: subtasks.length,
  team_size: 1 + team.support.length,
  assignment_summary: "Lead: " + team.lead + " + " + team.support.length + " support"
};`,
          outputVariables: [
            { name: "assigned_lead", type: "string" },
            { name: "assigned_support", type: "string" },
            { name: "subtasks_json", type: "string" },
            { name: "subtask_count", type: "number" },
            { name: "team_size", type: "number" },
            { name: "assignment_summary", type: "string" },
          ],
        },
      }),

      // 7. Notion dashboard sync
      n("7", "hitApi", "Sync event and tasks to Notion dashboard", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_api_key}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            {
              key: "parent",
              value: '{"database_id":"{notion_marketing_db_id}"}',
            },
            {
              key: "properties",
              value:
                '{"Name":{"title":[{"text":{"content":"{event_title}"}}]},"Type":{"select":{"name":"{event_type}"}},"Date":{"date":{"start":"{event_start}"}},"Lead":{"email":"{assigned_lead}"},"Jira Epic":{"url":"https://{jira_domain}.atlassian.net/browse/{jira_epic_key}"},"Status":{"select":{"name":"Not Started"}},"Team Size":{"number":{team_size}}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
            { name: "url", customName: "notion_page_url", type: "string" },
          ],
        },
      }),

      // 8. Slack kick-off notification
      slackNode(
        "8",
        "Send Slack kick-off notification",
        1720,
        ":calendar: *New Marketing Task Created*\\n*Event:* {event_title}\\n*Type:* {event_type}\\n*Date:* {event_start}\\n*Days until:* {days_until_event}\\n*Lead:* {assigned_lead}\\n*Team:* {assignment_summary}\\n*Jira:* {jira_epic_key}\\n*Notion:* {notion_page_url}\\n*Subtasks:* {subtask_count} tasks created",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Valid new event -> check for duplicates in Jira
      e("3", "8", "output_2"), // Invalid/past event -> notify only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1053. MARKETING MIX MODEL REPORTER (9 nodes)
  // ===================================================================

  "Marketing Mix Model Reporter": {
    summary:
      "Monthly data aggregation pulls paid media spend, organic and email metrics, joins revenue data in code, runs MMM regression calculations, generates AI strategic interpretations, compiles an interactive report to Sheets, sends leadership briefings via email, and handles data anomalies with a condition gate.",
    nodes: [
      trigger("Schedule: Monthly MMM report generation", "schedule", {
        schedule: "0 7 1 * * (First of each month at 7 AM)",
        howItStarts:
          "A monthly schedule triggers the marketing mix model analysis, aggregating spend and performance data across all channels to calculate contribution to revenue",
        setup:
          "Configure Google Ads and Meta Ads API credentials for spend data. Provide email analytics API (Mailchimp or Klaviyo) credentials. Set HubSpot API key for revenue data. Provide Google Sheets ID for report output and executive email list.",
        inputFields: [],
      }),

      // 2. Paid media spend fetch
      n("2", "hitApi", "Fetch paid media spend from Google Ads", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, metrics.cost_micros, metrics.conversions, metrics.conversion_value, metrics.clicks, metrics.impressions FROM campaign WHERE segments.date DURING LAST_30_DAYS",
            },
          ],
          variables: [
            {
              name: "results",
              customName: "paid_media_data_json",
              type: "string",
            },
            {
              name: "results.length",
              customName: "paid_campaign_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Organic + email metrics fetch
      n(
        "3",
        "hitApi",
        "Fetch email and organic metrics from Mailchimp",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://us1.api.mailchimp.com/3.0/reports?count=50&since_send_time={thirty_days_ago}",
            headers: [
              { key: "Authorization", value: "Bearer {mailchimp_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "reports",
                customName: "email_reports_json",
                type: "string",
              },
              {
                name: "total_items",
                customName: "email_campaign_count",
                type: "number",
              },
            ],
          },
        },
        480,
      ),

      // 4. Revenue data joiner (runCode)
      n("4", "runCode", "Join channel data with revenue for MMM input", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate and join all channel data for MMM analysis
const paidMedia = JSON.parse(vars.paid_media_data_json || "[]");
const emailReports = JSON.parse(vars.email_reports_json || "[]");

// Paid media aggregation
const totalPaidSpend = paidMedia.reduce((s, c) => s + ((c.metrics?.cost_micros || 0) / 1000000), 0);
const totalPaidConversions = paidMedia.reduce((s, c) => s + (c.metrics?.conversions || 0), 0);
const totalPaidRevenue = paidMedia.reduce((s, c) => s + (c.metrics?.conversion_value || 0), 0);

// Email metrics aggregation
const totalEmailSends = emailReports.reduce((s, r) => s + (r.emails_sent || 0), 0);
const totalEmailOpens = emailReports.reduce((s, r) => s + (r.opens?.unique_opens || 0), 0);
const totalEmailClicks = emailReports.reduce((s, r) => s + (r.clicks?.unique_subscriber_clicks || 0), 0);
const emailRevenue = totalEmailClicks * 12.5; // Avg revenue per click estimate

// Build MMM input matrix
const mmmInput = {
  paid_search_spend: totalPaidSpend * 0.6, // Estimate 60% search
  paid_social_spend: totalPaidSpend * 0.4, // Estimate 40% social
  email_sends: totalEmailSends,
  email_click_rate: totalEmailSends > 0 ? (totalEmailClicks / totalEmailSends * 100).toFixed(2) : "0",
  total_revenue: totalPaidRevenue + emailRevenue,
  data_points: paidMedia.length + emailReports.length
};

return {
  mmm_input_json: JSON.stringify(mmmInput),
  total_paid_spend: totalPaidSpend.toFixed(2),
  total_paid_conversions: totalPaidConversions,
  total_paid_revenue: totalPaidRevenue.toFixed(2),
  total_email_sends: totalEmailSends,
  total_email_clicks: totalEmailClicks,
  estimated_email_revenue: emailRevenue.toFixed(2),
  combined_revenue: (totalPaidRevenue + emailRevenue).toFixed(2),
  data_points_count: mmmInput.data_points
};`,
          outputVariables: [
            { name: "mmm_input_json", type: "string" },
            { name: "total_paid_spend", type: "string" },
            { name: "total_paid_conversions", type: "number" },
            { name: "total_paid_revenue", type: "string" },
            { name: "total_email_sends", type: "number" },
            { name: "total_email_clicks", type: "number" },
            { name: "estimated_email_revenue", type: "string" },
            { name: "combined_revenue", type: "string" },
            { name: "data_points_count", type: "number" },
          ],
        },
      }),

      // 5. MMM regression calculator (runCode)
      n("5", "runCode", "Run marketing mix model regression", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simplified MMM regression: calculate channel contribution percentages
const input = JSON.parse(vars.mmm_input_json || "{}");
const totalRevenue = parseFloat(vars.combined_revenue) || 0;
const totalPaidSpend = parseFloat(vars.total_paid_spend) || 0;
const paidRevenue = parseFloat(vars.total_paid_revenue) || 0;
const emailRevenue = parseFloat(vars.estimated_email_revenue) || 0;

// Channel contribution coefficients (simplified linear model)
const paidSearchContrib = (input.paid_search_spend || 0) > 0
  ? ((paidRevenue * 0.6) / totalRevenue * 100).toFixed(1) : "0";
const paidSocialContrib = (input.paid_social_spend || 0) > 0
  ? ((paidRevenue * 0.4) / totalRevenue * 100).toFixed(1) : "0";
const emailContrib = emailRevenue > 0
  ? (emailRevenue / totalRevenue * 100).toFixed(1) : "0";
const organicContrib = (100 - parseFloat(paidSearchContrib) - parseFloat(paidSocialContrib) - parseFloat(emailContrib)).toFixed(1);

// Efficiency metrics
const paidSearchCPA = input.paid_search_spend > 0 ? (input.paid_search_spend / (parseInt(vars.total_paid_conversions) * 0.6 || 1)).toFixed(2) : "N/A";
const paidSocialCPA = input.paid_social_spend > 0 ? (input.paid_social_spend / (parseInt(vars.total_paid_conversions) * 0.4 || 1)).toFixed(2) : "N/A";

return {
  paid_search_contribution: paidSearchContrib + "%",
  paid_social_contribution: paidSocialContrib + "%",
  email_contribution: emailContrib + "%",
  organic_contribution: organicContrib + "%",
  paid_search_cpa: paidSearchCPA,
  paid_social_cpa: paidSocialCPA,
  overall_roas: totalPaidSpend > 0 ? (totalRevenue / totalPaidSpend).toFixed(2) : "N/A",
  model_confidence: input.data_points > 20 ? "high" : input.data_points > 10 ? "medium" : "low",
  mmm_summary: "Paid Search: " + paidSearchContrib + "%, Paid Social: " + paidSocialContrib + "%, Email: " + emailContrib + "%, Organic: " + organicContrib + "%"
};`,
          outputVariables: [
            { name: "paid_search_contribution", type: "string" },
            { name: "paid_social_contribution", type: "string" },
            { name: "email_contribution", type: "string" },
            { name: "organic_contribution", type: "string" },
            { name: "paid_search_cpa", type: "string" },
            { name: "paid_social_cpa", type: "string" },
            { name: "overall_roas", type: "string" },
            { name: "model_confidence", type: "string" },
            { name: "mmm_summary", type: "string" },
          ],
        },
      }),

      // 6. AI strategic interpreter
      aiNode(
        "6",
        "AI: Interpret MMM results strategically",
        1000,
        "You are a marketing science advisor specializing in marketing mix modeling. Interpret MMM results for non-technical executives, translating statistical outputs into clear business recommendations. Return JSON.",
        "Monthly MMM results — Channel contributions: {mmm_summary}. Overall ROAS: {overall_roas}x. Paid Search CPA: ${paid_search_cpa}, Paid Social CPA: ${paid_social_cpa}. Model confidence: {model_confidence}. Total spend: ${total_paid_spend}, Total revenue: ${combined_revenue}. Generate strategic_narrative (3-5 sentences for board-level communication), budget_action_items (3 specific budget reallocation recommendations), diminishing_returns_warning (which channels may be saturated), next_month_forecast (expected revenue range if current mix maintained), methodology_note (brief model limitations caveat).",
        [
          {
            field: "strategic_narrative",
            as: "ai_strategic_narrative",
            type: "string",
          },
          {
            field: "budget_action_items",
            as: "ai_budget_actions",
            type: "string",
          },
          {
            field: "diminishing_returns_warning",
            as: "ai_saturation_warning",
            type: "string",
          },
          { field: "next_month_forecast", as: "ai_forecast", type: "string" },
          {
            field: "methodology_note",
            as: "ai_methodology_note",
            type: "string",
          },
        ],
      ),

      // 7. Interactive report (Sheets)
      sheetsNode(
        "7",
        "Compile interactive MMM report to Sheets",
        1240,
        "MMMReport",
        '[["{timestamp}","{paid_search_contribution}","{paid_social_contribution}","{email_contribution}","{organic_contribution}","{total_paid_spend}","{combined_revenue}","{overall_roas}","{paid_search_cpa}","{paid_social_cpa}","{model_confidence}","{ai_budget_actions}"]]',
      ),

      // 8. Leadership briefing email
      emailNode(
        "8",
        "Send leadership MMM briefing",
        1480,
        "{executive_email_list}",
        "Monthly Marketing Mix Model: {overall_roas}x Overall ROAS",
        "Marketing Mix Model Report — {report_month}\\n\\n{ai_strategic_narrative}\\n\\nChannel Contribution Breakdown:\\n- Paid Search: {paid_search_contribution} (CPA: ${paid_search_cpa})\\n- Paid Social: {paid_social_contribution} (CPA: ${paid_social_cpa})\\n- Email: {email_contribution}\\n- Organic: {organic_contribution}\\n\\nOverall ROAS: {overall_roas}x on ${total_paid_spend} spend\\nTotal Revenue: ${combined_revenue}\\n\\nRecommended Actions:\\n{ai_budget_actions}\\n\\nSaturation Warning: {ai_saturation_warning}\\n\\nNext Month Forecast: {ai_forecast}\\n\\nNote: {ai_methodology_note}\\n\\nFull interactive report: {sheets_url}",
      ),

      // 9. Data anomaly handler condition
      n(
        "9",
        "condition",
        "Is model confidence sufficient for decision-making?",
        1720,
        {
          conditionData: [
            {
              variable: "model_confidence",
              value: "low",
              comparison_operator: "NOT EQUAL",
              logical_operator: "AND",
            },
            {
              variable: "data_points_count",
              value: "5",
              comparison_operator: "GREATER THAN",
              logical_operator: "END",
            },
          ],
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1054. MARKETING SPEND TRACKER (8 nodes)
  // ===================================================================

  "Marketing Spend Tracker": {
    summary:
      "Daily spend ingestion pulls budget data from Google Sheets, calculates spend-vs-budget pacing in code, evaluates threshold alerts via condition, generates AI budget recommendations, sends Slack pacing alerts for moderate issues, emails critical overspend alerts, and archives spend logs to Sheets.",
    nodes: [
      trigger("Schedule: Daily marketing spend check", "schedule", {
        schedule: "0 8 * * * (Daily at 8 AM)",
        howItStarts:
          "A daily schedule ingests the latest marketing spend data across all platforms and compares against monthly budget allocations to detect pacing issues early",
        setup:
          "Configure Google Sheets API credentials to read budget allocations spreadsheet. Provide Google Ads, Meta Ads, and LinkedIn Ads API credentials for live spend data. Set up Slack webhook for pacing alerts and email addresses for critical overspend notifications.",
        inputFields: [],
      }),

      // 2. Budget lookup (Sheets)
      n("2", "hitApi", "Fetch monthly budgets from Google Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{budget_spreadsheet_id}/values/Budgets!A2:E20",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            { name: "values", customName: "budget_rows_json", type: "string" },
            { name: "range", customName: "budget_range", type: "string" },
          ],
        },
      }),

      // 3. Spend-vs-budget calculator (runCode)
      n("3", "runCode", "Calculate spend pacing vs budget", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate spend pacing against monthly budgets
const budgetRows = JSON.parse(vars.budget_rows_json || "[]");
const now = new Date();
const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
const dayOfMonth = now.getDate();
const pacingTarget = dayOfMonth / daysInMonth; // Expected % of budget spent

const channels = budgetRows.map(row => {
  const channel = row[0] || "Unknown";
  const monthlyBudget = parseFloat(row[1]) || 0;
  const currentSpend = parseFloat(row[2]) || 0;
  const spendPct = monthlyBudget > 0 ? currentSpend / monthlyBudget : 0;
  const pacingDiff = spendPct - pacingTarget;
  let status = "on_track";
  if (pacingDiff > 0.15) status = "critical_overspend";
  else if (pacingDiff > 0.08) status = "overspending";
  else if (pacingDiff < -0.15) status = "underspending";
  return { channel, monthly_budget: monthlyBudget, current_spend: currentSpend, spend_pct: (spendPct * 100).toFixed(1), pacing_target: (pacingTarget * 100).toFixed(1), pacing_diff: (pacingDiff * 100).toFixed(1), status };
});

const critical = channels.filter(c => c.status === "critical_overspend");
const overspending = channels.filter(c => c.status === "overspending");
const totalBudget = channels.reduce((s, c) => s + c.monthly_budget, 0);
const totalSpend = channels.reduce((s, c) => s + c.current_spend, 0);

return {
  channels_json: JSON.stringify(channels),
  total_budget: totalBudget.toFixed(2),
  total_spend: totalSpend.toFixed(2),
  overall_pacing: totalBudget > 0 ? ((totalSpend / totalBudget) * 100).toFixed(1) : "0",
  pacing_target: (pacingTarget * 100).toFixed(1),
  critical_count: critical.length,
  overspend_count: overspending.length,
  has_critical: critical.length > 0 ? "yes" : "no",
  pacing_summary: "Day " + dayOfMonth + "/" + daysInMonth + ": " + ((totalSpend / totalBudget) * 100).toFixed(1) + "% spent vs " + (pacingTarget * 100).toFixed(1) + "% target"
};`,
          outputVariables: [
            { name: "channels_json", type: "string" },
            { name: "total_budget", type: "string" },
            { name: "total_spend", type: "string" },
            { name: "overall_pacing", type: "string" },
            { name: "pacing_target", type: "string" },
            { name: "critical_count", type: "number" },
            { name: "overspend_count", type: "number" },
            { name: "has_critical", type: "string" },
            { name: "pacing_summary", type: "string" },
          ],
        },
      }),

      // 4. Threshold alert condition
      n("4", "condition", "Is spend pacing within acceptable range?", 760, {
        conditionData: [
          {
            variable: "critical_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
          {
            variable: "overspend_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 5. AI recommendation engine
      aiNode(
        "5",
        "AI: Generate budget pacing recommendations",
        1000,
        "You are a media buying expert. Analyze spend pacing data and provide specific, actionable recommendations to optimize budget utilization. Be direct about what to pause, scale, or adjust. Return JSON.",
        "Marketing spend pacing: {pacing_summary}. Channel breakdown: {channels_json}. Critical overspend channels: {critical_count}. Overspending channels: {overspend_count}. Total budget: ${total_budget}, Total spent: ${total_spend}. Return pacing_assessment (1-2 sentence summary), immediate_actions (list of urgent budget adjustments), channel_specific_recs (per-channel recommendations), projected_month_end_spend (forecast if no changes made), savings_opportunity (potential $ saved with recommended adjustments).",
        [
          {
            field: "pacing_assessment",
            as: "ai_pacing_assessment",
            type: "string",
          },
          {
            field: "immediate_actions",
            as: "ai_immediate_actions",
            type: "string",
          },
          {
            field: "channel_specific_recs",
            as: "ai_channel_recs",
            type: "string",
          },
          {
            field: "projected_month_end_spend",
            as: "ai_projected_spend",
            type: "string",
          },
          { field: "savings_opportunity", as: "ai_savings", type: "string" },
        ],
      ),

      // 6. Slack pacing alert
      slackNode(
        "6",
        "Send Slack pacing alert",
        1240,
        ":chart_with_upwards_trend: *Marketing Spend Pacing Alert*\\n{pacing_summary}\\n\\n*Assessment:* {ai_pacing_assessment}\\n*Critical channels:* {critical_count}\\n*Overspending channels:* {overspend_count}\\n\\n*Immediate Actions:*\\n{ai_immediate_actions}\\n\\n*Projected Month-End:* {ai_projected_spend}\\n*Savings Opportunity:* {ai_savings}",
      ),

      // 7. Email for critical overspend
      emailNode(
        "7",
        "Send critical overspend email alert",
        1480,
        "{finance_team_email}",
        "CRITICAL: Marketing Overspend Alert — {pacing_summary}",
        "Critical Marketing Spend Alert\\n\\n{ai_pacing_assessment}\\n\\nCurrent Status:\\n- Total Budget: ${total_budget}\\n- Total Spent: ${total_spend} ({overall_pacing}% of budget)\\n- Pacing Target: {pacing_target}%\\n- Critical Overspend Channels: {critical_count}\\n\\nImmediate Actions Required:\\n{ai_immediate_actions}\\n\\nChannel Recommendations:\\n{ai_channel_recs}\\n\\nProjected Month-End Spend: {ai_projected_spend}\\nPotential Savings: {ai_savings}\\n\\nPlease review and approve budget adjustments ASAP.",
      ),

      // 8. Spend log archiver (Sheets)
      sheetsNode(
        "8",
        "Archive daily spend log to Sheets",
        1720,
        "SpendTracker",
        '[["{timestamp}","{total_budget}","{total_spend}","{overall_pacing}","{pacing_target}","{critical_count}","{overspend_count}","{has_critical}","{ai_pacing_assessment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Overspend detected -> generate recommendations
      e("4", "8", "output_2"), // On track -> archive only
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1055. MARKETO ABM CAMPAIGN ORCHESTRATION (10 nodes)
  // ===================================================================

  "Marketo ABM Campaign Orchestration": {
    summary:
      "Loads target account list, scores account engagement in code, routes by account tier via condition, generates AI-personalized content, deploys Marketo campaigns, syncs LinkedIn ABM ads, notifies sales via Slack, detects meeting triggers, updates CRM opportunities in Salesforce, and logs campaign performance to Sheets.",
    nodes: [
      trigger("Webhook: ABM target account list updated", "webhook_app", {
        source: "Salesforce / CRM",
        event: "account.list.updated",
        howItStarts:
          "A target account list is updated or refreshed in the CRM, triggering ABM campaign orchestration across Marketo, LinkedIn, and sales channels",
        setup:
          "Configure Salesforce webhook to POST account list updates. Provide Marketo REST API credentials (client ID, client secret, endpoint). Set LinkedIn Marketing API access token. Configure Slack webhook for sales alerts and Google Sheets ID for performance tracking.",
        inputFields: [],
      }),

      // 2. Account engagement scorer (runCode)
      n("2", "runCode", "Score account engagement and classify tiers", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score target accounts based on engagement signals
const payload = vars.webhook_payload || "{}";
const data = typeof payload === "string" ? JSON.parse(payload) : payload;
const accounts = data.accounts || data.records || [];

const scored = accounts.map(a => {
  const webVisits = parseInt(a.web_visits_30d) || 0;
  const emailEngagement = parseInt(a.email_opens_30d) || 0;
  const adClicks = parseInt(a.ad_clicks_30d) || 0;
  const contentDownloads = parseInt(a.content_downloads) || 0;
  const score = (webVisits * 2) + (emailEngagement * 3) + (adClicks * 5) + (contentDownloads * 10);
  let tier = "Tier 3";
  if (score >= 100) tier = "Tier 1";
  else if (score >= 40) tier = "Tier 2";
  return {
    account_id: a.id || a.account_id,
    account_name: a.name || a.account_name,
    domain: a.domain || a.website,
    score, tier, webVisits, emailEngagement, adClicks, contentDownloads
  };
});

const tier1 = scored.filter(a => a.tier === "Tier 1");
const tier2 = scored.filter(a => a.tier === "Tier 2");
const tier3 = scored.filter(a => a.tier === "Tier 3");

return {
  scored_accounts_json: JSON.stringify(scored),
  tier1_count: tier1.length,
  tier2_count: tier2.length,
  tier3_count: tier3.length,
  total_accounts: scored.length,
  has_tier1: tier1.length > 0 ? "yes" : "no",
  tier_summary: tier1.length + " Tier 1, " + tier2.length + " Tier 2, " + tier3.length + " Tier 3",
  top_account_name: tier1.length > 0 ? tier1[0].account_name : (tier2.length > 0 ? tier2[0].account_name : "")
};`,
          outputVariables: [
            { name: "scored_accounts_json", type: "string" },
            { name: "tier1_count", type: "number" },
            { name: "tier2_count", type: "number" },
            { name: "tier3_count", type: "number" },
            { name: "total_accounts", type: "number" },
            { name: "has_tier1", type: "string" },
            { name: "tier_summary", type: "string" },
            { name: "top_account_name", type: "string" },
          ],
        },
      }),

      // 3. Account tier router condition
      n("3", "condition", "Are there Tier 1 high-priority accounts?", 520, {
        conditionData: [
          {
            variable: "tier1_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 4. AI personalized content
      aiNode(
        "4",
        "AI: Generate tier-based ABM content",
        760,
        "You are an ABM strategist. Create highly personalized campaign content for target accounts. Tier 1 gets executive-level white-glove content; Tier 2 gets industry-specific nurture content; Tier 3 gets awareness content. Return JSON.",
        "Target accounts: {tier_summary}. Top account: {top_account_name}. Scored data: {scored_accounts_json}. Generate tier1_email_subject, tier1_email_body (HTML, executive invitation to exclusive briefing), tier2_email_subject, tier2_email_body (HTML, industry insights with CTA), tier3_ad_headline (LinkedIn awareness ad), tier3_ad_copy (LinkedIn ad body text), marketo_campaign_name (descriptive name for this ABM wave).",
        [
          { field: "tier1_email_subject", as: "tier1_subject", type: "string" },
          { field: "tier1_email_body", as: "tier1_body", type: "string" },
          { field: "tier2_email_subject", as: "tier2_subject", type: "string" },
          { field: "tier2_email_body", as: "tier2_body", type: "string" },
          { field: "tier3_ad_headline", as: "tier3_headline", type: "string" },
          { field: "tier3_ad_copy", as: "tier3_ad_copy", type: "string" },
          {
            field: "marketo_campaign_name",
            as: "marketo_campaign_name",
            type: "string",
          },
        ],
      ),

      // 5. Marketo campaign deployer
      n("5", "hitApi", "Deploy ABM campaign via Marketo", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_endpoint}.mktorest.com/rest/v1/campaigns/{marketo_campaign_id}/trigger.json",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "input",
              value:
                '{"leads":[{"id":"{marketo_lead_id}"}],"tokens":[{"name":"{{my.ABM_Subject}}","value":"{tier1_subject}"},{"name":"{{my.ABM_Body}}","value":"{tier1_body}"}]}',
            },
          ],
          variables: [
            {
              name: "result[0].id",
              customName: "marketo_campaign_run_id",
              type: "string",
            },
            {
              name: "success",
              customName: "marketo_deploy_success",
              type: "boolean",
            },
          ],
        },
      }),

      // 6. LinkedIn ABM ad sync
      n("6", "hitApi", "Sync ABM audiences to LinkedIn Ads", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/rest/adAccounts/{linkedin_ad_account_id}/adCampaigns",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_access_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "LinkedIn-Version", value: "202401" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "name", value: "ABM - {marketo_campaign_name}" },
            { key: "type", value: "SPONSORED_UPDATES" },
            { key: "status", value: "ACTIVE" },
            {
              key: "targeting",
              value:
                '{"includedTargetingFacets":{"employers":["{top_account_name}"]}}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_campaign_id", type: "string" },
            {
              name: "status",
              customName: "linkedin_campaign_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Sales alignment notifier (Slack)
      slackNode(
        "7",
        "Notify sales team of ABM campaign launch",
        1480,
        ":dart: *ABM Campaign Launched*\\n*Campaign:* {marketo_campaign_name}\\n*Accounts:* {tier_summary}\\n*Tier 1 VIPs:* {tier1_count} accounts receiving executive outreach\\n*Top Account:* {top_account_name}\\n*Marketo:* Campaign deployed (ID: {marketo_campaign_run_id})\\n*LinkedIn:* ABM ads active (ID: {linkedin_campaign_id})\\n\\nSales: Watch for engagement signals from Tier 1 accounts this week.",
      ),

      // 8. Meeting trigger detector
      n(
        "8",
        "hitApi",
        "Check for meeting request signals in Salesforce",
        1720,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{salesforce_instance}.salesforce.com/services/data/v59.0/query/?q=SELECT+Id,AccountId,Subject,Status+FROM+Event+WHERE+CreatedDate=LAST_N_DAYS:1+AND+Subject+LIKE+'%ABM%'",
            headers: [
              { key: "Authorization", value: "Bearer {salesforce_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "records",
                customName: "meeting_signals_json",
                type: "string",
              },
              {
                name: "totalSize",
                customName: "meeting_signal_count",
                type: "number",
              },
            ],
          },
        },
      ),

      // 9. CRM opportunity updater (Salesforce)
      n("9", "hitApi", "Update Salesforce opportunities with ABM data", 1960, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://{salesforce_instance}.salesforce.com/services/data/v59.0/sobjects/Account/{account_id}",
          headers: [
            { key: "Authorization", value: "Bearer {salesforce_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "ABM_Campaign_Status__c", value: "Active" },
            { key: "ABM_Tier__c", value: "{account_tier}" },
            { key: "ABM_Last_Campaign_Date__c", value: "{timestamp}" },
            { key: "ABM_Engagement_Score__c", value: "{account_score}" },
          ],
          variables: [
            { name: "id", customName: "updated_account_id", type: "string" },
            {
              name: "success",
              customName: "sf_update_success",
              type: "boolean",
            },
          ],
        },
      }),

      // 10. Campaign performance (Sheets)
      sheetsNode(
        "10",
        "Log ABM campaign performance to Sheets",
        2200,
        "ABMPerformance",
        '[["{timestamp}","{marketo_campaign_name}","{total_accounts}","{tier_summary}","{marketo_deploy_success}","{linkedin_campaign_id}","{meeting_signal_count}","{top_account_name}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Tier 1 accounts exist -> full ABM orchestration
      e("3", "6", "output_2"), // No Tier 1 -> LinkedIn ads only for lower tiers
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1056. MARKETO EMAIL PERFORMANCE REPORTER (8 nodes)
  // ===================================================================

  "Marketo Email Performance Reporter": {
    summary:
      "Weekly Marketo data extraction pulls email metrics, aggregates performance in code, compares against benchmarks, uses AI to analyze results, checks deliverability health via condition, compiles a dashboard to Sheets, sends team reports via email, and alerts Slack on critical issues.",
    nodes: [
      trigger("Schedule: Weekly Marketo email performance", "schedule", {
        schedule: "0 7 * * 1 (Monday at 7 AM)",
        howItStarts:
          "A weekly schedule extracts email performance data from Marketo, analyzing open rates, click rates, deliverability, and engagement trends across all active campaigns",
        setup:
          "Configure Marketo REST API credentials (client ID, client secret, base URL). Define benchmark thresholds for open rate (>20%), click rate (>3%), bounce rate (<2%), unsubscribe rate (<0.5%). Provide Google Sheets ID and Slack webhook.",
        inputFields: [],
      }),

      // 2. Marketo data extraction
      n("2", "hitApi", "Extract email stats from Marketo", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{marketo_endpoint}.mktorest.com/rest/v1/emails.json?status=approved&maxReturn=200",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "result",
              customName: "marketo_emails_json",
              type: "string",
            },
            {
              name: "result.length",
              customName: "total_email_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Metrics aggregation (runCode)
      n("3", "runCode", "Aggregate email performance metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate Marketo email performance into summary metrics
const emails = JSON.parse(vars.marketo_emails_json || "[]");
let totalSent = 0, totalOpens = 0, totalClicks = 0, totalBounces = 0, totalUnsubs = 0;
const emailStats = emails.map(em => {
  const sent = em.statistics?.sent || em.numSent || 0;
  const opens = em.statistics?.opens || em.numOpens || 0;
  const clicks = em.statistics?.clicks || em.numClicks || 0;
  const bounces = em.statistics?.bounces || em.numBounces || 0;
  const unsubs = em.statistics?.unsubscribes || em.numUnsubs || 0;
  totalSent += sent; totalOpens += opens; totalClicks += clicks; totalBounces += bounces; totalUnsubs += unsubs;
  return {
    name: em.name, id: em.id, sent, opens, clicks, bounces, unsubs,
    open_rate: sent > 0 ? (opens / sent * 100).toFixed(1) : "0",
    click_rate: sent > 0 ? (clicks / sent * 100).toFixed(1) : "0",
    bounce_rate: sent > 0 ? (bounces / sent * 100).toFixed(1) : "0"
  };
});

const avgOpenRate = totalSent > 0 ? (totalOpens / totalSent * 100).toFixed(1) : "0";
const avgClickRate = totalSent > 0 ? (totalClicks / totalSent * 100).toFixed(1) : "0";
const avgBounceRate = totalSent > 0 ? (totalBounces / totalSent * 100).toFixed(1) : "0";
const avgUnsubRate = totalSent > 0 ? (totalUnsubs / totalSent * 100).toFixed(1) : "0";
const deliverability = totalSent > 0 ? ((1 - totalBounces / totalSent) * 100).toFixed(1) : "100";

return {
  email_stats_json: JSON.stringify(emailStats),
  total_sent: totalSent, total_opens: totalOpens, total_clicks: totalClicks,
  avg_open_rate: avgOpenRate, avg_click_rate: avgClickRate,
  avg_bounce_rate: avgBounceRate, avg_unsub_rate: avgUnsubRate,
  deliverability_score: deliverability,
  is_healthy: parseFloat(avgBounceRate) < 2 && parseFloat(avgUnsubRate) < 0.5 ? "yes" : "no",
  performance_summary: "Sent: " + totalSent + ", Open: " + avgOpenRate + "%, Click: " + avgClickRate + "%, Bounce: " + avgBounceRate + "%"
};`,
          outputVariables: [
            { name: "email_stats_json", type: "string" },
            { name: "total_sent", type: "number" },
            { name: "total_opens", type: "number" },
            { name: "total_clicks", type: "number" },
            { name: "avg_open_rate", type: "string" },
            { name: "avg_click_rate", type: "string" },
            { name: "avg_bounce_rate", type: "string" },
            { name: "avg_unsub_rate", type: "string" },
            { name: "deliverability_score", type: "string" },
            { name: "is_healthy", type: "string" },
            { name: "performance_summary", type: "string" },
          ],
        },
      }),

      // 4. AI performance analyst
      aiNode(
        "4",
        "AI: Analyze email performance trends",
        760,
        "You are a Marketo email marketing analyst. Evaluate email performance against industry benchmarks and identify opportunities for improvement. Be specific about which emails need attention. Return JSON.",
        "Marketo email performance this week: {performance_summary}. Deliverability: {deliverability_score}%. Avg open rate: {avg_open_rate}% (benchmark: 20%), Avg click rate: {avg_click_rate}% (benchmark: 3%), Bounce rate: {avg_bounce_rate}% (threshold: 2%), Unsub rate: {avg_unsub_rate}% (threshold: 0.5%). Email details: {email_stats_json}. Return overall_grade (A-F), trend_analysis (2-3 sentences on week-over-week direction), top_performers (best 3 emails by engagement), underperformers (worst 3 emails needing attention), optimization_tips (3 specific improvement actions), deliverability_assessment (healthy/warning/critical with reason).",
        [
          { field: "overall_grade", as: "ai_grade", type: "string" },
          { field: "trend_analysis", as: "ai_trend_analysis", type: "string" },
          { field: "top_performers", as: "ai_top_performers", type: "string" },
          {
            field: "underperformers",
            as: "ai_underperformers",
            type: "string",
          },
          {
            field: "optimization_tips",
            as: "ai_optimization_tips",
            type: "string",
          },
          {
            field: "deliverability_assessment",
            as: "ai_deliverability_status",
            type: "string",
          },
        ],
      ),

      // 5. Deliverability health check condition
      n("5", "condition", "Is email deliverability healthy?", 1000, {
        conditionData: [
          {
            variable: "is_healthy",
            value: "yes",
            comparison_operator: "EQUAL",
            logical_operator: "END",
          },
        ],
      }),

      // 6. Performance dashboard (Sheets)
      sheetsNode(
        "6",
        "Update email performance dashboard in Sheets",
        1240,
        "MarketoPerformance",
        '[["{timestamp}","{total_sent}","{avg_open_rate}","{avg_click_rate}","{avg_bounce_rate}","{avg_unsub_rate}","{deliverability_score}","{ai_grade}","{ai_trend_analysis}"]]',
      ),

      // 7. Team report email
      emailNode(
        "7",
        "Send weekly email performance report",
        1480,
        "{marketing_team_email}",
        "Marketo Weekly Report: Grade {ai_grade} — {avg_open_rate}% Open Rate",
        "Marketo Email Performance Report\\n\\nOverall Grade: {ai_grade}\\n\\n{ai_trend_analysis}\\n\\nKey Metrics:\\n- Emails Sent: {total_sent}\\n- Open Rate: {avg_open_rate}% (benchmark: 20%)\\n- Click Rate: {avg_click_rate}% (benchmark: 3%)\\n- Bounce Rate: {avg_bounce_rate}% (threshold: <2%)\\n- Unsubscribe Rate: {avg_unsub_rate}% (threshold: <0.5%)\\n- Deliverability Score: {deliverability_score}%\\n\\nTop Performers:\\n{ai_top_performers}\\n\\nNeeds Attention:\\n{ai_underperformers}\\n\\nOptimization Tips:\\n{ai_optimization_tips}\\n\\nDeliverability: {ai_deliverability_status}\\n\\nFull dashboard: {sheets_url}",
      ),

      // 8. Critical issue alert (Slack)
      slackNode(
        "8",
        "Alert Slack on critical email issues",
        1720,
        ":rotating_light: *Marketo Deliverability Alert*\\nGrade: {ai_grade}\\nDeliverability: {deliverability_score}%\\nBounce Rate: {avg_bounce_rate}%\\nUnsub Rate: {avg_unsub_rate}%\\n\\n*Status:* {ai_deliverability_status}\\n*Action Required:* {ai_optimization_tips}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Healthy -> standard report
      e("5", "8", "output_2"), // Unhealthy -> Slack alert first
      e("6", "7"),
      e("8", "6"),
    ],
  },

  // ===================================================================
  // 1057. MARKETO EVENT FOLLOW-UP SEQUENCE (9 nodes)
  // ===================================================================

  "Marketo Event Follow-Up Sequence": {
    summary:
      "Syncs event attendees, segments by engagement level in code, generates AI-personalized follow-ups, sends thank-you emails to hot leads, nurtures warm leads with a delayed email, re-engages no-shows, hands off qualified leads to HubSpot sales, alerts sales via Slack, and tracks event ROI in Sheets.",
    nodes: [
      trigger("Webhook: Event attendee sync from Marketo", "webhook_app", {
        source: "Marketo",
        event: "program.membership.updated",
        howItStarts:
          "Event attendee data is synced from Marketo after an event concludes, triggering segmented follow-up sequences based on engagement level",
        setup:
          "Configure Marketo webhook to POST program membership changes when event status changes to 'Attended', 'No Show', or 'Registered'. Provide HubSpot API key for sales handoff. Set up Twilio for SMS and Google Sheets ID for ROI tracking.",
        inputFields: [],
      }),

      // 2. Engagement level segmentation (runCode)
      n("2", "runCode", "Segment attendees by engagement level", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment event attendees into hot, warm, and no-show groups
const payload = vars.webhook_payload || "{}";
const data = typeof payload === "string" ? JSON.parse(payload) : payload;
const attendees = data.members || data.leads || [];

const hot = []; // Attended + engaged (asked questions, visited booth, etc.)
const warm = []; // Attended but passive
const noShow = []; // Registered but didn't attend

attendees.forEach(a => {
  const status = (a.status || a.progressionStatus || "").toLowerCase();
  const engagementScore = parseInt(a.engagement_score || a.score) || 0;
  const attended = status.includes("attended") || status.includes("present");
  const registered = status.includes("registered") || status.includes("invited");

  if (attended && engagementScore > 50) {
    hot.push({ ...a, segment: "hot" });
  } else if (attended) {
    warm.push({ ...a, segment: "warm" });
  } else if (registered) {
    noShow.push({ ...a, segment: "no_show" });
  }
});

return {
  hot_leads_json: JSON.stringify(hot),
  warm_leads_json: JSON.stringify(warm),
  noshow_leads_json: JSON.stringify(noShow),
  hot_count: hot.length,
  warm_count: warm.length,
  noshow_count: noShow.length,
  total_registrants: attendees.length,
  attendance_rate: attendees.length > 0 ? (((hot.length + warm.length) / attendees.length) * 100).toFixed(1) : "0",
  segment_summary: hot.length + " hot, " + warm.length + " warm, " + noShow.length + " no-show"
};`,
          outputVariables: [
            { name: "hot_leads_json", type: "string" },
            { name: "warm_leads_json", type: "string" },
            { name: "noshow_leads_json", type: "string" },
            { name: "hot_count", type: "number" },
            { name: "warm_count", type: "number" },
            { name: "noshow_count", type: "number" },
            { name: "total_registrants", type: "number" },
            { name: "attendance_rate", type: "string" },
            { name: "segment_summary", type: "string" },
          ],
        },
      }),

      // 3. AI personalized follow-up
      aiNode(
        "3",
        "AI: Generate segment-specific follow-up content",
        520,
        "You are an event marketing specialist. Create differentiated follow-up content for hot leads (enthusiastic thank you + meeting request), warm leads (value-add nurture), and no-shows (recap + FOMO). Return JSON.",
        "Event follow-up for segments: {segment_summary}. Attendance rate: {attendance_rate}%. Generate hot_subject (thank you with meeting CTA), hot_body (HTML personalized thank-you, references their engagement, includes calendar link), warm_subject (value follow-up), warm_body (HTML with session highlights, resource links, soft CTA), noshow_subject (you missed it), noshow_body (HTML recap with recording link, key takeaways, next event invite), event_roi_notes (engagement quality assessment).",
        [
          { field: "hot_subject", as: "hot_email_subject", type: "string" },
          { field: "hot_body", as: "hot_email_body", type: "string" },
          { field: "warm_subject", as: "warm_email_subject", type: "string" },
          { field: "warm_body", as: "warm_email_body", type: "string" },
          {
            field: "noshow_subject",
            as: "noshow_email_subject",
            type: "string",
          },
          { field: "noshow_body", as: "noshow_email_body", type: "string" },
          { field: "event_roi_notes", as: "ai_roi_notes", type: "string" },
        ],
      ),

      // 4. Hot lead thank-you email
      emailNode(
        "4",
        "Send thank-you email to hot leads",
        760,
        "{hot_lead_email}",
        "{hot_email_subject}",
        "{hot_email_body}",
      ),

      // 5. Warm lead nurture email (delay)
      n("5", "delayAction", "Wait 2 days before warm lead nurture", 1000, {
        delayDetails: { duration: 2, unit: "days" },
      }),

      // 6. No-show re-engagement email (uses warm delay path)
      emailNode(
        "6",
        "Send re-engagement email to no-shows",
        1240,
        "{noshow_lead_email}",
        "{noshow_email_subject}",
        "{noshow_email_body}",
      ),

      // 7. Sales handoff (HubSpot)
      hubspotPostNode(
        "7",
        "Hand off hot leads to sales in HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{hot_lead_email}","firstname":"{hot_lead_firstname}","lastname":"{hot_lead_lastname}","lifecyclestage":"salesqualifiedlead","hs_lead_status":"OPEN","event_engagement":"hot","event_attendance":"attended","event_follow_up_date":"{timestamp}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "handed_off_email",
            type: "string",
          },
        ],
      ),

      // 8. Slack sales alert
      slackNode(
        "8",
        "Alert sales team of hot event leads",
        1720,
        ":fire: *Hot Event Leads Ready for Sales*\\n*Event Segments:* {segment_summary}\\n*Hot Leads:* {hot_count} handed off to HubSpot\\n*Attendance Rate:* {attendance_rate}%\\n\\nHot leads have been auto-assigned in HubSpot with lifecycle stage 'Sales Qualified Lead'. Please prioritize outreach within 24 hours.\\n\\n*ROI Notes:* {ai_roi_notes}",
      ),

      // 9. Event ROI tracker (Sheets)
      sheetsNode(
        "9",
        "Log event ROI metrics to Sheets",
        1960,
        "EventROI",
        '[["{timestamp}","{total_registrants}","{attendance_rate}","{hot_count}","{warm_count}","{noshow_count}","{segment_summary}","{ai_roi_notes}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1058. MARKETO NURTURE CAMPAIGN BUILDER (10 nodes)
  // ===================================================================

  "Marketo Nurture Campaign Builder": {
    summary:
      "Accepts new leads, scores them in code, routes by nurture track via condition, personalizes content with AI, builds Marketo engagement programs, creates email assets, monitors stage transitions, alerts sales on MQLs via Slack, updates Salesforce leads, and logs nurture performance to Sheets.",
    nodes: [
      trigger("Webhook: New lead intake for nurture", "webhook_app", {
        source: "Marketo / Web Form",
        event: "lead.created",
        howItStarts:
          "A new lead enters the system via form submission, import, or API sync, triggering automated nurture track assignment and campaign enrollment",
        setup:
          "Configure Marketo webhook for new lead notifications. Provide Marketo REST API credentials for program and email creation. Set Salesforce API credentials for lead sync. Configure Slack webhook for MQL alerts and Google Sheets ID.",
        inputFields: [],
      }),

      // 2. Lead scoring (runCode)
      n("2", "runCode", "Score lead and determine nurture track", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score new lead and assign to appropriate nurture track
const payload = vars.webhook_payload || "{}";
const lead = typeof payload === "string" ? JSON.parse(payload) : payload;

const email = lead.email || "";
const company = lead.company || "";
const title = (lead.title || lead.jobTitle || "").toLowerCase();
const industry = lead.industry || "";
const formSource = lead.source || lead.leadSource || "";

// Demographic scoring
let demoScore = 0;
if (title.includes("vp") || title.includes("director") || title.includes("cxo") || title.includes("chief")) demoScore += 30;
else if (title.includes("manager") || title.includes("head")) demoScore += 20;
else if (title.includes("specialist") || title.includes("analyst")) demoScore += 10;
if (company) demoScore += 10;
if (industry) demoScore += 5;

// Behavioral scoring
let behaviorScore = 0;
if (formSource === "demo_request") behaviorScore += 40;
else if (formSource === "content_download") behaviorScore += 20;
else if (formSource === "webinar_registration") behaviorScore += 15;
else if (formSource === "newsletter_signup") behaviorScore += 5;

const totalScore = demoScore + behaviorScore;
let nurture_track = "awareness"; // Default
if (totalScore >= 60) nurture_track = "decision";
else if (totalScore >= 30) nurture_track = "consideration";

const isMQL = totalScore >= 70;

return {
  lead_email: email,
  lead_company: company,
  lead_title: title,
  lead_score: totalScore,
  demo_score: demoScore,
  behavior_score: behaviorScore,
  nurture_track: nurture_track,
  is_mql: isMQL ? "yes" : "no",
  lead_source: formSource,
  scoring_summary: "Score " + totalScore + " (" + demoScore + " demo + " + behaviorScore + " behavior) -> " + nurture_track + " track"
};`,
          outputVariables: [
            { name: "lead_email", type: "string" },
            { name: "lead_company", type: "string" },
            { name: "lead_title", type: "string" },
            { name: "lead_score", type: "number" },
            { name: "demo_score", type: "number" },
            { name: "behavior_score", type: "number" },
            { name: "nurture_track", type: "string" },
            { name: "is_mql", type: "string" },
            { name: "lead_source", type: "string" },
            { name: "scoring_summary", type: "string" },
          ],
        },
      }),

      // 3. Nurture track router condition
      n("3", "condition", "Is lead scored as MQL (score >= 70)?", 520, {
        conditionData: [
          {
            variable: "is_mql",
            value: "yes",
            comparison_operator: "EQUAL",
            logical_operator: "END",
          },
        ],
      }),

      // 4. AI content personalizer
      aiNode(
        "4",
        "AI: Personalize nurture content for track",
        760,
        "You are a B2B content marketing specialist. Create stage-appropriate nurture email content. Awareness = educational thought leadership; Consideration = case studies and comparisons; Decision = demos, ROI calculators, social proof. Return JSON.",
        "Lead details — Email: {lead_email}, Company: {lead_company}, Title: {lead_title}, Score: {lead_score}, Track: {nurture_track}, Source: {lead_source}. Generate welcome_email_subject, welcome_email_body (HTML track-appropriate welcome with relevant CTA), nurture_stage_1_subject, nurture_stage_1_body (HTML first nurture touch, 3 days after welcome), nurture_stage_2_subject, nurture_stage_2_body (HTML second nurture touch, 7 days after first), engagement_program_name (descriptive Marketo program name).",
        [
          {
            field: "welcome_email_subject",
            as: "welcome_subject",
            type: "string",
          },
          { field: "welcome_email_body", as: "welcome_body", type: "string" },
          {
            field: "nurture_stage_1_subject",
            as: "stage1_subject",
            type: "string",
          },
          { field: "nurture_stage_1_body", as: "stage1_body", type: "string" },
          {
            field: "nurture_stage_2_subject",
            as: "stage2_subject",
            type: "string",
          },
          { field: "nurture_stage_2_body", as: "stage2_body", type: "string" },
          {
            field: "engagement_program_name",
            as: "program_name",
            type: "string",
          },
        ],
      ),

      // 5. Marketo engagement program builder
      n("5", "hitApi", "Create Marketo engagement program", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_endpoint}.mktorest.com/rest/asset/v1/programs.json",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{program_name}" },
            { key: "type", value: "Engagement" },
            { key: "channel", value: "Nurture" },
            {
              key: "folder",
              value: '{"id":{marketo_folder_id},"type":"Folder"}',
            },
            {
              key: "description",
              value:
                "Auto-built nurture for {nurture_track} track leads. Score threshold: {lead_score}.",
            },
          ],
          variables: [
            {
              name: "result[0].id",
              customName: "marketo_program_id",
              type: "string",
            },
            {
              name: "success",
              customName: "marketo_program_created",
              type: "boolean",
            },
          ],
        },
      }),

      // 6. Email asset creator
      n("6", "hitApi", "Create email assets in Marketo", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_endpoint}.mktorest.com/rest/asset/v1/emails.json",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{program_name} - Welcome" },
            { key: "subject", value: "{welcome_subject}" },
            { key: "fromName", value: "{company_name}" },
            { key: "fromEmail", value: "{from_email}" },
            { key: "template", value: "{marketo_template_id}" },
            {
              key: "folder",
              value: '{"id":{marketo_folder_id},"type":"Folder"}',
            },
          ],
          variables: [
            {
              name: "result[0].id",
              customName: "marketo_email_asset_id",
              type: "string",
            },
            {
              name: "success",
              customName: "marketo_email_created",
              type: "boolean",
            },
          ],
        },
      }),

      // 7. Stage transition monitor
      n("7", "hitApi", "Monitor lead stage transitions in Marketo", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://{marketo_endpoint}.mktorest.com/rest/v1/activities.json?activityTypeIds=12,13&sinceDatetime={one_hour_ago}&listId={marketo_nurture_list_id}",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "result",
              customName: "stage_transitions_json",
              type: "string",
            },
            {
              name: "result.length",
              customName: "transition_count",
              type: "number",
            },
          ],
        },
      }),

      // 8. Sales MQL alert (Slack)
      slackNode(
        "8",
        "Alert sales on new MQL from nurture",
        1720,
        ":star: *New MQL from Nurture Campaign*\\n*Lead:* {lead_email}\\n*Company:* {lead_company}\\n*Title:* {lead_title}\\n*Score:* {lead_score} ({scoring_summary})\\n*Track:* {nurture_track}\\n*Source:* {lead_source}\\n*Marketo Program:* {program_name} (ID: {marketo_program_id})\\n\\nPlease follow up within 24 hours.",
      ),

      // 9. Salesforce lead update
      n("9", "hitApi", "Sync lead to Salesforce", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://{salesforce_instance}.salesforce.com/services/data/v59.0/sobjects/Lead",
          headers: [
            { key: "Authorization", value: "Bearer {salesforce_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "Email", value: "{lead_email}" },
            { key: "Company", value: "{lead_company}" },
            { key: "Title", value: "{lead_title}" },
            { key: "LeadSource", value: "{lead_source}" },
            { key: "Lead_Score__c", value: "{lead_score}" },
            { key: "Nurture_Track__c", value: "{nurture_track}" },
            { key: "Status", value: "{is_mql}" },
            { key: "Marketo_Program_ID__c", value: "{marketo_program_id}" },
          ],
          variables: [
            { name: "id", customName: "salesforce_lead_id", type: "string" },
            { name: "success", customName: "sf_lead_created", type: "boolean" },
          ],
        },
      }),

      // 10. Nurture performance (Sheets)
      sheetsNode(
        "10",
        "Log nurture enrollment to Sheets",
        2200,
        "NurturePerformance",
        '[["{timestamp}","{lead_email}","{lead_company}","{lead_score}","{nurture_track}","{is_mql}","{program_name}","{marketo_program_id}","{salesforce_lead_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "8", "output_1"), // MQL -> immediate sales alert
      e("3", "4", "output_2"), // Not MQL -> nurture track
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "9"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1059. MARKETO SMART CAMPAIGN TRIGGER (8 nodes)
  // ===================================================================

  "Marketo Smart Campaign Trigger": {
    summary:
      "Receives activity webhooks, classifies activity types in code, evaluates trigger rules via condition, activates Marketo smart campaigns, sends real-time sales alerts to Slack, logs CRM activities in Salesforce, enforces rate limits and suppression in code, and handles errors with a fallback node.",
    nodes: [
      trigger("Webhook: Marketo activity event", "webhook_app", {
        source: "Marketo",
        event: "activity.triggered",
        howItStarts:
          "A real-time activity event webhook fires from Marketo when a lead performs a high-intent action (page visit, form fill, email click, program status change)",
        setup:
          "Configure Marketo webhook subscriptions for activity types: Visit Web Page (1), Click Link in Email (3), Fill Out Form (2), Change Score (22). Provide Marketo REST API credentials, Salesforce API token, and Slack webhook URL.",
        inputFields: [],
      }),

      // 2. Activity type classifier (runCode)
      n("2", "runCode", "Classify activity type and priority", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify Marketo activity and determine trigger priority
const payload = vars.webhook_payload || "{}";
const activity = typeof payload === "string" ? JSON.parse(payload) : payload;

const activityType = activity.activityTypeId || activity.type_id || 0;
const leadId = activity.leadId || activity.lead_id || "";
const leadEmail = activity.primaryAttributeValue || activity.email || "";
const activityDate = activity.activityDate || new Date().toISOString();
const attributes = activity.attributes || activity.primaryAttribute || {};

const typeMap = { 1: "page_visit", 2: "form_fill", 3: "email_click", 22: "score_change" };
const typeName = typeMap[activityType] || "unknown";

let priority = "low";
let shouldTrigger = false;
if (typeName === "form_fill") { priority = "high"; shouldTrigger = true; }
else if (typeName === "email_click") { priority = "medium"; shouldTrigger = true; }
else if (typeName === "score_change" && parseInt(attributes.newValue || 0) >= 100) { priority = "high"; shouldTrigger = true; }
else if (typeName === "page_visit" && (attributes.webpage || "").includes("pricing")) { priority = "high"; shouldTrigger = true; }

return {
  activity_type: typeName,
  activity_type_id: activityType,
  lead_id: leadId,
  lead_email: leadEmail,
  activity_date: activityDate,
  priority: priority,
  should_trigger: shouldTrigger ? "yes" : "no",
  activity_detail: JSON.stringify(attributes),
  classification_summary: typeName + " | Priority: " + priority + " | Lead: " + leadEmail
};`,
          outputVariables: [
            { name: "activity_type", type: "string" },
            { name: "activity_type_id", type: "number" },
            { name: "lead_id", type: "string" },
            { name: "lead_email", type: "string" },
            { name: "activity_date", type: "string" },
            { name: "priority", type: "string" },
            { name: "should_trigger", type: "string" },
            { name: "activity_detail", type: "string" },
            { name: "classification_summary", type: "string" },
          ],
        },
      }),

      // 3. Trigger rule evaluator condition
      n(
        "3",
        "condition",
        "Should this activity trigger a smart campaign?",
        520,
        {
          conditionData: [
            {
              variable: "should_trigger",
              value: "yes",
              comparison_operator: "EQUAL",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 4. Marketo smart campaign activator
      n("4", "hitApi", "Activate Marketo smart campaign for lead", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{marketo_endpoint}.mktorest.com/rest/v1/campaigns/{marketo_smart_campaign_id}/trigger.json",
          headers: [
            { key: "Authorization", value: "Bearer {marketo_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "input",
              value:
                '{"leads":[{"id":"{lead_id}"}],"tokens":[{"name":"{{my.Activity_Type}}","value":"{activity_type}"},{"name":"{{my.Priority}}","value":"{priority}"}]}',
            },
          ],
          variables: [
            {
              name: "result[0].id",
              customName: "marketo_trigger_id",
              type: "string",
            },
            {
              name: "success",
              customName: "marketo_trigger_success",
              type: "boolean",
            },
          ],
        },
      }),

      // 5. Real-time sales alert (Slack)
      slackNode(
        "5",
        "Send real-time sales alert to Slack",
        1000,
        ":zap: *Real-Time Lead Alert*\\n*Lead:* {lead_email}\\n*Activity:* {activity_type}\\n*Priority:* {priority}\\n*Detail:* {activity_detail}\\n*Time:* {activity_date}\\n*Marketo Campaign:* Triggered (ID: {marketo_trigger_id})\\n\\n{classification_summary}",
      ),

      // 6. CRM activity logger (Salesforce)
      n("6", "hitApi", "Log activity to Salesforce", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{salesforce_instance}.salesforce.com/services/data/v59.0/sobjects/Task",
          headers: [
            { key: "Authorization", value: "Bearer {salesforce_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "Subject", value: "Marketo Activity: {activity_type}" },
            {
              key: "Description",
              value:
                "Lead {lead_email} performed {activity_type} (priority: {priority}). Detail: {activity_detail}. Marketo campaign triggered: {marketo_trigger_success}.",
            },
            { key: "Priority", value: "{priority}" },
            { key: "Status", value: "Completed" },
            { key: "ActivityDate", value: "{activity_date}" },
            { key: "WhoId", value: "{salesforce_lead_id}" },
          ],
          variables: [
            { name: "id", customName: "sf_task_id", type: "string" },
            { name: "success", customName: "sf_log_success", type: "boolean" },
          ],
        },
      }),

      // 7. Rate limit + suppression guard (runCode)
      n("7", "runCode", "Enforce rate limits and suppression rules", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Rate limiting and suppression check
const leadEmail = vars.lead_email || "";
const activityType = vars.activity_type || "";
const now = Date.now();

// Simple in-memory rate limit check (in production, use Redis)
const rateLimitKey = leadEmail + "_" + activityType;
const maxTriggersPerHour = 3;
const maxTriggersPerDay = 10;

// Check suppression list patterns
const suppressionDomains = ["test.com", "example.com", "mailinator.com"];
const isSuppressed = suppressionDomains.some(d => leadEmail.endsWith("@" + d));

// Check if lead email is valid
const isValidEmail = leadEmail.includes("@") && leadEmail.includes(".");

return {
  rate_limit_key: rateLimitKey,
  is_suppressed: isSuppressed ? "yes" : "no",
  is_valid_email: isValidEmail ? "yes" : "no",
  max_hourly_triggers: maxTriggersPerHour,
  max_daily_triggers: maxTriggersPerDay,
  suppression_reason: isSuppressed ? "Domain in suppression list" : (isValidEmail ? "none" : "Invalid email format"),
  rate_check_timestamp: new Date(now).toISOString(),
  guard_status: !isSuppressed && isValidEmail ? "passed" : "blocked"
};`,
          outputVariables: [
            { name: "rate_limit_key", type: "string" },
            { name: "is_suppressed", type: "string" },
            { name: "is_valid_email", type: "string" },
            { name: "max_hourly_triggers", type: "number" },
            { name: "max_daily_triggers", type: "number" },
            { name: "suppression_reason", type: "string" },
            { name: "rate_check_timestamp", type: "string" },
            { name: "guard_status", type: "string" },
          ],
        },
      }),

      // 8. Error fallback handler
      slackNode(
        "8",
        "Send error fallback notification",
        1720,
        ":warning: *Marketo Trigger Guard Alert*\\n*Lead:* {lead_email}\\n*Status:* {guard_status}\\n*Suppression:* {suppression_reason}\\n*Activity:* {activity_type} ({priority})\\n*Rate Limit Key:* {rate_limit_key}\\n\\nReview suppression rules and rate limits if triggers are being blocked unexpectedly.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Should trigger -> activate campaign
      e("3", "7", "output_2"), // Should not trigger -> check suppression
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1060. MEMBER RENEWAL REMINDER CAMPAIGN (9 nodes)
  // ===================================================================

  "Member Renewal Reminder Campaign": {
    summary:
      "Daily renewal scan identifies members due for renewal, classifies member value in code, generates AI renewal content, sends a 30-day notice email, follows with a 14-day social proof email after delay, dispatches a 7-day urgency SMS via Twilio, sends a 1-day final notice, updates renewal status in HubSpot, and escalates lapsed members.",
    nodes: [
      trigger("Schedule: Daily renewal scan", "schedule", {
        schedule: "0 8 * * * (Daily at 8 AM)",
        howItStarts:
          "A daily schedule scans for members with renewals due within the next 30 days, triggering a multi-touch reminder campaign with increasing urgency",
        setup:
          "Configure HubSpot API key with contacts read/write access and custom properties for membership_expiry_date, membership_tier, and total_lifetime_value. Provide Twilio credentials for SMS. Set up email templates and Slack webhook for escalation.",
        inputFields: [],
      }),

      // 2. Member value classifier (runCode)
      n("2", "runCode", "Classify member renewal priority by value", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify members approaching renewal by value and urgency
const members = JSON.parse(vars.renewal_members_json || "[]");
const now = Date.now();
const dayMs = 86400000;

const classified = members.map(m => {
  const expiryDate = new Date(m.membership_expiry_date || 0).getTime();
  const daysUntilExpiry = Math.ceil((expiryDate - now) / dayMs);
  const ltv = parseFloat(m.total_lifetime_value) || 0;
  const tier = m.membership_tier || "Standard";

  let priority = "standard";
  if (ltv > 5000 || tier === "Enterprise") priority = "vip";
  else if (ltv > 1000 || tier === "Professional") priority = "high";

  let urgencyLevel = "30_day";
  if (daysUntilExpiry <= 1) urgencyLevel = "final_notice";
  else if (daysUntilExpiry <= 7) urgencyLevel = "7_day";
  else if (daysUntilExpiry <= 14) urgencyLevel = "14_day";

  return {
    email: m.email, name: m.firstname || "Member", phone: m.phone || "",
    days_until_expiry: daysUntilExpiry, ltv, tier, priority, urgencyLevel,
    contact_id: m.hs_object_id || m.id
  };
});

const vips = classified.filter(m => m.priority === "vip");
const highPriority = classified.filter(m => m.priority === "high");
const urgent = classified.filter(m => m.daysUntilExpiry <= 7);

return {
  classified_members_json: JSON.stringify(classified),
  total_renewals: classified.length,
  vip_count: vips.length,
  high_priority_count: highPriority.length,
  urgent_count: urgent.length,
  sample_email: classified.length > 0 ? classified[0].email : "",
  sample_name: classified.length > 0 ? classified[0].name : "",
  sample_phone: classified.length > 0 ? classified[0].phone : "",
  renewal_summary: classified.length + " renewals (" + vips.length + " VIP, " + urgent.length + " urgent)"
};`,
          outputVariables: [
            { name: "classified_members_json", type: "string" },
            { name: "total_renewals", type: "number" },
            { name: "vip_count", type: "number" },
            { name: "high_priority_count", type: "number" },
            { name: "urgent_count", type: "number" },
            { name: "sample_email", type: "string" },
            { name: "sample_name", type: "string" },
            { name: "sample_phone", type: "string" },
            { name: "renewal_summary", type: "string" },
          ],
        },
      }),

      // 3. AI renewal content
      aiNode(
        "3",
        "AI: Generate renewal reminder content",
        520,
        "You are a retention marketing specialist. Create compelling renewal reminders with escalating urgency. 30-day = friendly reminder with benefits recap. 14-day = social proof and FOMO. 7-day = urgency with potential loss. Final = last chance with special offer. Return JSON.",
        "Renewal campaign: {renewal_summary}. Generate thirty_day_subject, thirty_day_body (HTML friendly reminder with benefits they will lose), fourteen_day_subject, fourteen_day_body (HTML social proof — peer stats, community highlights), seven_day_sms (under 160 chars, urgent tone with renewal link), final_notice_subject, final_notice_body (HTML last chance with exclusive retention discount).",
        [
          { field: "thirty_day_subject", as: "day30_subject", type: "string" },
          { field: "thirty_day_body", as: "day30_body", type: "string" },
          {
            field: "fourteen_day_subject",
            as: "day14_subject",
            type: "string",
          },
          { field: "fourteen_day_body", as: "day14_body", type: "string" },
          { field: "seven_day_sms", as: "day7_sms", type: "string" },
          {
            field: "final_notice_subject",
            as: "final_subject",
            type: "string",
          },
          { field: "final_notice_body", as: "final_body", type: "string" },
        ],
      ),

      // 4. 30-day notice email
      emailNode(
        "4",
        "Send 30-day renewal notice email",
        760,
        "{sample_email}",
        "{day30_subject}",
        "{day30_body}",
      ),

      // 5. 14-day social proof email (delay)
      n("5", "delayAction", "Wait 16 days for 14-day notice", 1000, {
        delayDetails: { duration: 16, unit: "days" },
      }),

      // 6. 7-day urgency SMS (Twilio)
      smsNode(
        "6",
        "Send 7-day urgency SMS via Twilio",
        1240,
        "{sample_phone}",
        "{day7_sms}",
      ),

      // 7. 1-day final notice email
      emailNode(
        "7",
        "Send final renewal notice email",
        1480,
        "{sample_email}",
        "{final_subject}",
        "{final_body}",
      ),

      // 8. Renewal status tracker (HubSpot)
      hubspotPostNode(
        "8",
        "Update renewal status in HubSpot",
        1720,
        "crm/v3/objects/contacts/batch/update",
        [
          {
            key: "inputs",
            value:
              '[{"id":"{contact_id}","properties":{"renewal_campaign_status":"reminders_sent","renewal_reminder_count":"4","last_renewal_reminder_date":"{timestamp}","renewal_priority":"{priority}"}}]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "updated_contact_id",
            type: "string",
          },
          {
            name: "status",
            customName: "hubspot_renewal_status",
            type: "string",
          },
        ],
      ),

      // 9. Lapsed member escalation
      slackNode(
        "9",
        "Escalate lapsed members to retention team",
        1960,
        ":rotating_light: *Lapsed Member Escalation*\\n*Renewal Summary:* {renewal_summary}\\n*VIP Members at Risk:* {vip_count}\\n*Urgent (< 7 days):* {urgent_count}\\n*Total Reminders Sent:* 4-touch sequence completed\\n\\nAll reminder emails and SMS have been sent. Members who have not renewed need manual outreach from the retention team.\\n\\nHubSpot update status: {hubspot_renewal_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1061. META ADS CAMPAIGN LAUNCH (8 nodes)
  // ===================================================================

  "Meta Ads Campaign Launch": {
    summary:
      "Accepts a campaign brief, generates AI ad copy, builds audiences in code, creates campaign structure via Meta API, configures ad sets, uploads ad creatives, activates and validates the campaign, and sends a Slack launch notification.",
    nodes: [
      trigger("Webhook: New campaign brief submitted", "webhook_app", {
        source: "Internal Tool / Form",
        event: "campaign.brief.submitted",
        howItStarts:
          "A campaign brief is submitted via internal tool or form, triggering automated Meta Ads campaign creation with AI-generated copy and audience targeting",
        setup:
          "Configure webhook to receive campaign briefs with fields: objective, budget, targeting_notes, product_description, landing_page_url. Provide Meta Marketing API access token, ad account ID, and Facebook Page ID. Set up Slack webhook for launch notifications.",
        inputFields: [],
      }),

      // 2. AI ad copy generation
      aiNode(
        "2",
        "AI: Generate Meta ad copy variants",
        280,
        "You are a performance marketing copywriter specializing in Meta Ads. Create scroll-stopping ad copy that drives action. Follow Meta Ads best practices: short primary text, clear headlines, strong CTAs. Return JSON.",
        "Campaign brief — Payload: {webhook_payload}. Find the objective, budget, targeting notes, product description, and landing page URL regardless of field names. Generate primary_text_1 (125 chars max, emotional hook), primary_text_2 (125 chars max, benefit-focused), headline_1 (40 chars, action-oriented), headline_2 (40 chars, curiosity-driven), description (30 chars, supporting CTA), cta_type (one of: LEARN_MORE, SHOP_NOW, SIGN_UP, BOOK_TRAVEL, CONTACT_US), campaign_objective (CONVERSIONS, TRAFFIC, or LEAD_GENERATION), daily_budget (number based on brief), target_audience_description.",
        [
          { field: "primary_text_1", as: "ad_primary_1", type: "string" },
          { field: "primary_text_2", as: "ad_primary_2", type: "string" },
          { field: "headline_1", as: "ad_headline_1", type: "string" },
          { field: "headline_2", as: "ad_headline_2", type: "string" },
          { field: "description", as: "ad_description", type: "string" },
          { field: "cta_type", as: "ad_cta_type", type: "string" },
          {
            field: "campaign_objective",
            as: "campaign_objective",
            type: "string",
          },
          { field: "daily_budget", as: "daily_budget", type: "number" },
          {
            field: "target_audience_description",
            as: "audience_description",
            type: "string",
          },
        ],
      ),

      // 3. Audience builder (runCode)
      n("3", "runCode", "Build targeting audience specification", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build Meta Ads targeting specification from AI output
const audienceDesc = vars.audience_description || "";
const objective = vars.campaign_objective || "CONVERSIONS";

// Map audience description to targeting spec
let ageMin = 25, ageMax = 55;
let genders = [0]; // 0 = all
const interests = [];
const behaviors = [];

const desc = audienceDesc.toLowerCase();
if (desc.includes("young") || desc.includes("millennial")) { ageMin = 18; ageMax = 35; }
else if (desc.includes("professional") || desc.includes("business")) { ageMin = 28; ageMax = 55; }
else if (desc.includes("senior") || desc.includes("executive")) { ageMin = 35; ageMax = 65; }

if (desc.includes("tech") || desc.includes("software")) interests.push({ id: 6003139266461, name: "Technology" });
if (desc.includes("business") || desc.includes("entrepreneur")) interests.push({ id: 6003012781372, name: "Business" });
if (desc.includes("fitness") || desc.includes("health")) interests.push({ id: 6003384677166, name: "Fitness and wellness" });
if (desc.includes("ecommerce") || desc.includes("shopping")) behaviors.push({ id: 6002714895372, name: "Engaged shoppers" });

if (interests.length === 0) interests.push({ id: 6003012781372, name: "Business" });

const targetingSpec = {
  age_min: ageMin, age_max: ageMax, genders,
  geo_locations: { countries: ["US"], location_types: ["home"] },
  flexible_spec: [{ interests, behaviors: behaviors.length > 0 ? behaviors : undefined }]
};

return {
  targeting_spec_json: JSON.stringify(targetingSpec),
  age_range: ageMin + "-" + ageMax,
  interest_count: interests.length,
  behavior_count: behaviors.length,
  audience_summary: "Ages " + ageMin + "-" + ageMax + ", " + interests.length + " interests, " + behaviors.length + " behaviors",
  budget_cents: (parseInt(vars.daily_budget) || 50) * 100
};`,
          outputVariables: [
            { name: "targeting_spec_json", type: "string" },
            { name: "age_range", type: "string" },
            { name: "interest_count", type: "number" },
            { name: "behavior_count", type: "number" },
            { name: "audience_summary", type: "string" },
            { name: "budget_cents", type: "number" },
          ],
        },
      }),

      // 4. Campaign structure creator
      n("4", "hitApi", "Create Meta Ads campaign", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{meta_ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Auto-Campaign: {ad_headline_1} — {timestamp}",
            },
            { key: "objective", value: "{campaign_objective}" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
          ],
          variables: [
            { name: "id", customName: "meta_campaign_id", type: "string" },
          ],
        },
      }),

      // 5. Ad set configuration
      n("5", "hitApi", "Configure Meta ad set with targeting", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{meta_ad_account_id}/adsets",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "AdSet: {audience_summary}" },
            { key: "campaign_id", value: "{meta_campaign_id}" },
            { key: "daily_budget", value: "{budget_cents}" },
            { key: "billing_event", value: "IMPRESSIONS" },
            { key: "optimization_goal", value: "OFFSITE_CONVERSIONS" },
            { key: "targeting", value: "{targeting_spec_json}" },
            { key: "status", value: "PAUSED" },
          ],
          variables: [
            { name: "id", customName: "meta_adset_id", type: "string" },
          ],
        },
      }),

      // 6. Ad creative uploader
      n("6", "hitApi", "Upload ad creative to Meta", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{meta_ad_account_id}/ads",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Ad: {ad_headline_1}" },
            { key: "adset_id", value: "{meta_adset_id}" },
            {
              key: "creative",
              value:
                '{"object_story_spec":{"page_id":"{meta_page_id}","link_data":{"message":"{ad_primary_1}","name":"{ad_headline_1}","description":"{ad_description}","link":"{landing_page_url}","call_to_action":{"type":"{ad_cta_type}"}}}}',
            },
            { key: "status", value: "PAUSED" },
          ],
          variables: [{ name: "id", customName: "meta_ad_id", type: "string" }],
        },
      }),

      // 7. Campaign activation + validation
      n("7", "hitApi", "Activate and validate Meta campaign", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_campaign_id}",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "status", value: "ACTIVE" }],
          variables: [
            {
              name: "success",
              customName: "campaign_activated",
              type: "boolean",
            },
          ],
        },
      }),

      // 8. Launch notification (Slack)
      slackNode(
        "8",
        "Send campaign launch notification to Slack",
        1720,
        ":rocket: *Meta Ads Campaign Launched*\\n*Campaign:* {ad_headline_1}\\n*Objective:* {campaign_objective}\\n*Budget:* ${daily_budget}/day\\n*Audience:* {audience_summary}\\n*Ad Copy:* {ad_primary_1}\\n*CTA:* {ad_cta_type}\\n\\n*IDs:*\\n- Campaign: {meta_campaign_id}\\n- Ad Set: {meta_adset_id}\\n- Ad: {meta_ad_id}\\n\\nStatus: {campaign_activated}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1062. META CREATIVE DIGEST (8 nodes)
  // ===================================================================

  "Meta Creative Digest": {
    summary:
      "Weekly pull of Meta ad creatives, calculates ROAS and ranks them in code, detects creative fatigue, generates AI creative analysis, compiles a performance report to Sheets, highlights top performers, sends fatigue alerts and rotation recommendations to Slack, and archives historical data.",
    nodes: [
      trigger("Schedule: Weekly Meta creative analysis", "schedule", {
        schedule: "0 9 * * 1 (Monday at 9 AM)",
        howItStarts:
          "A weekly schedule pulls all active ad creative performance data from Meta, analyzing ROAS, engagement, and fatigue indicators to recommend creative rotations",
        setup:
          "Configure Meta Marketing API access token and ad account ID. Define fatigue thresholds: frequency > 4, CTR drop > 20% week-over-week. Provide Google Sheets ID for reports and Slack webhook for alerts.",
        inputFields: [],
      }),

      // 2. ROAS calc + ranker (runCode)
      n("2", "runCode", "Calculate ROAS and rank creatives", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Fetch and rank Meta ad creatives by ROAS
const creatives = JSON.parse(vars.meta_creatives_json || "[]");

const ranked = creatives.map(c => {
  const spend = parseFloat(c.spend) || 0;
  const revenue = parseFloat(c.action_values?.find(a => a.action_type === "offsite_conversion.fb_pixel_purchase")?.value || 0);
  const impressions = parseInt(c.impressions) || 0;
  const clicks = parseInt(c.clicks) || 0;
  const frequency = parseFloat(c.frequency) || 0;
  const ctr = impressions > 0 ? (clicks / impressions * 100) : 0;
  const roas = spend > 0 ? revenue / spend : 0;
  const cpc = clicks > 0 ? spend / clicks : 0;

  return {
    ad_id: c.ad_id, ad_name: c.ad_name,
    spend: spend.toFixed(2), revenue: revenue.toFixed(2),
    roas: roas.toFixed(2), ctr: ctr.toFixed(2),
    cpc: cpc.toFixed(2), frequency: frequency.toFixed(1),
    impressions, clicks
  };
}).sort((a, b) => parseFloat(b.roas) - parseFloat(a.roas));

const topPerformers = ranked.slice(0, 5);
const bottomPerformers = ranked.slice(-3);
const avgROAS = ranked.length > 0 ? (ranked.reduce((s, r) => s + parseFloat(r.roas), 0) / ranked.length).toFixed(2) : "0";

return {
  ranked_creatives_json: JSON.stringify(ranked),
  top_performers_json: JSON.stringify(topPerformers),
  bottom_performers_json: JSON.stringify(bottomPerformers),
  total_creatives: ranked.length,
  avg_roas: avgROAS,
  best_creative_name: topPerformers.length > 0 ? topPerformers[0].ad_name : "N/A",
  best_roas: topPerformers.length > 0 ? topPerformers[0].roas : "0",
  total_spend: ranked.reduce((s, r) => s + parseFloat(r.spend), 0).toFixed(2),
  ranking_summary: ranked.length + " creatives ranked, Avg ROAS: " + avgROAS + "x, Best: " + (topPerformers[0]?.roas || "0") + "x"
};`,
          outputVariables: [
            { name: "ranked_creatives_json", type: "string" },
            { name: "top_performers_json", type: "string" },
            { name: "bottom_performers_json", type: "string" },
            { name: "total_creatives", type: "number" },
            { name: "avg_roas", type: "string" },
            { name: "best_creative_name", type: "string" },
            { name: "best_roas", type: "string" },
            { name: "total_spend", type: "string" },
            { name: "ranking_summary", type: "string" },
          ],
        },
      }),

      // 3. Fatigue detector (runCode)
      n("3", "runCode", "Detect creative fatigue signals", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect creative fatigue based on frequency and CTR degradation
const creatives = JSON.parse(vars.ranked_creatives_json || "[]");
const fatigueThresholdFreq = 4;
const fatigueThresholdCTR = 0.5; // Below 0.5% CTR is fatigued

const fatigued = creatives.filter(c => {
  const freq = parseFloat(c.frequency);
  const ctr = parseFloat(c.ctr);
  return freq > fatigueThresholdFreq || ctr < fatigueThresholdCTR;
});

const healthy = creatives.filter(c => {
  const freq = parseFloat(c.frequency);
  const ctr = parseFloat(c.ctr);
  return freq <= fatigueThresholdFreq && ctr >= fatigueThresholdCTR;
});

const wastedSpend = fatigued.reduce((s, c) => s + parseFloat(c.spend), 0);

return {
  fatigued_creatives_json: JSON.stringify(fatigued),
  healthy_creatives_json: JSON.stringify(healthy),
  fatigued_count: fatigued.length,
  healthy_count: healthy.length,
  wasted_spend_on_fatigued: wastedSpend.toFixed(2),
  fatigue_rate: creatives.length > 0 ? ((fatigued.length / creatives.length) * 100).toFixed(1) : "0",
  has_fatigued: fatigued.length > 0 ? "yes" : "no",
  fatigue_summary: fatigued.length + " fatigued (" + ((fatigued.length / (creatives.length || 1)) * 100).toFixed(0) + "%), $" + wastedSpend.toFixed(2) + " potentially wasted"
};`,
          outputVariables: [
            { name: "fatigued_creatives_json", type: "string" },
            { name: "healthy_creatives_json", type: "string" },
            { name: "fatigued_count", type: "number" },
            { name: "healthy_count", type: "number" },
            { name: "wasted_spend_on_fatigued", type: "string" },
            { name: "fatigue_rate", type: "string" },
            { name: "has_fatigued", type: "string" },
            { name: "fatigue_summary", type: "string" },
          ],
        },
      }),

      // 4. AI creative analyst
      aiNode(
        "4",
        "AI: Analyze creative performance patterns",
        760,
        "You are a creative strategist for performance marketing. Analyze ad creative performance data, identify patterns in top-performing vs underperforming creatives, and recommend specific creative directions. Return JSON.",
        "Creative performance — {ranking_summary}. Fatigue: {fatigue_summary}. Top performers: {top_performers_json}. Bottom performers: {bottom_performers_json}. Fatigued creatives: {fatigued_creatives_json}. Generate creative_health_score (1-100), winning_patterns (what top creatives have in common), losing_patterns (what bottom creatives share), rotation_recommendations (specific creatives to pause/replace), new_creative_directions (3 fresh concept ideas based on winners), estimated_savings ($ saved by pausing fatigued creatives).",
        [
          {
            field: "creative_health_score",
            as: "ai_health_score",
            type: "number",
          },
          {
            field: "winning_patterns",
            as: "ai_winning_patterns",
            type: "string",
          },
          {
            field: "losing_patterns",
            as: "ai_losing_patterns",
            type: "string",
          },
          {
            field: "rotation_recommendations",
            as: "ai_rotation_recs",
            type: "string",
          },
          {
            field: "new_creative_directions",
            as: "ai_new_directions",
            type: "string",
          },
          {
            field: "estimated_savings",
            as: "ai_estimated_savings",
            type: "string",
          },
        ],
      ),

      // 5. Performance report (Sheets)
      sheetsNode(
        "5",
        "Compile creative performance report to Sheets",
        1000,
        "CreativeDigest",
        '[["{timestamp}","{total_creatives}","{avg_roas}","{best_creative_name}","{best_roas}","{fatigued_count}","{fatigue_rate}","{wasted_spend_on_fatigued}","{ai_health_score}","{total_spend}"]]',
      ),

      // 6. Top performers highlight email
      emailNode(
        "6",
        "Send top performer creative highlights",
        1240,
        "{creative_team_email}",
        "Creative Digest: {best_roas}x Best ROAS — {ai_health_score}/100 Health Score",
        "Weekly Meta Creative Digest\\n\\nCreative Health Score: {ai_health_score}/100\\n\\nPerformance Summary:\\n- Total Creatives: {total_creatives}\\n- Average ROAS: {avg_roas}x\\n- Best Performer: {best_creative_name} ({best_roas}x ROAS)\\n- Total Spend: ${total_spend}\\n\\nWinning Patterns:\\n{ai_winning_patterns}\\n\\nNew Creative Directions:\\n{ai_new_directions}\\n\\nFatigue Alert:\\n{fatigue_summary}\\n\\nRotation Recommendations:\\n{ai_rotation_recs}\\n\\nEstimated Savings: {ai_estimated_savings}\\n\\nFull report: {sheets_url}",
      ),

      // 7. Fatigue alert + rotation recommendation (Slack)
      slackNode(
        "7",
        "Send creative fatigue alert to Slack",
        1480,
        ":art: *Creative Performance Digest*\\nHealth Score: {ai_health_score}/100\\n\\n*Top Performers:*\\n- Best: {best_creative_name} ({best_roas}x ROAS)\\n- Avg ROAS: {avg_roas}x\\n\\n*Fatigue Alert:* {fatigue_summary}\\n*Wasted Spend:* ${wasted_spend_on_fatigued}\\n\\n*Rotation Recs:* {ai_rotation_recs}\\n*New Directions:* {ai_new_directions}\\n*Savings Opportunity:* {ai_estimated_savings}",
      ),

      // 8. Historical archive (Sheets)
      sheetsNode(
        "8",
        "Archive creative data for historical analysis",
        1720,
        "CreativeArchive",
        '[["{timestamp}","{ranked_creatives_json}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1063. META CUSTOM AUDIENCE SYNC (7 nodes)
  // ===================================================================

  "Meta Custom Audience Sync": {
    summary:
      "Extracts CRM segments from HubSpot, prepares and hashes user data in code, validates PII compliance via condition, uploads to Meta Custom Audiences API, monitors audience match rates, confirms sync to Sheets, and notifies via Slack.",
    nodes: [
      // 1. CRM segment extraction (HubSpot)
      trigger("Schedule: Daily CRM audience sync", "schedule", {
        schedule: "0 6 * * * (Daily at 6 AM)",
        howItStarts:
          "A daily schedule extracts high-value customer segments from HubSpot CRM for synchronization to Meta Custom Audiences, enabling targeted ad delivery",
        setup:
          "Configure HubSpot API key with contacts read access. Provide Meta Marketing API access token and ad account ID. Define segment criteria (e.g., lifecycle stage = customer, total revenue > $100). Provide Google Sheets ID and Slack webhook.",
        inputFields: [],
      }),

      // 2. Data preparation + hashing (runCode)
      n("2", "runCode", "Prepare and hash user data for Meta", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare CRM contacts for Meta Custom Audience upload with SHA256 hashing
const contacts = JSON.parse(vars.hubspot_contacts_json || "[]");

// Meta requires SHA256-hashed PII fields
function sha256(str) {
  // In production, use crypto.createHash('sha256').update(str.toLowerCase().trim()).digest('hex')
  // Placeholder: actual hashing happens at runtime
  return "sha256_" + str.toLowerCase().trim().replace(/[^a-z0-9@.]/g, "");
}

const prepared = contacts.map(c => {
  const email = c.properties?.email || "";
  const phone = (c.properties?.phone || "").replace(/[^0-9+]/g, "");
  const firstName = c.properties?.firstname || "";
  const lastName = c.properties?.lastname || "";

  return {
    email_hash: email ? sha256(email) : "",
    phone_hash: phone ? sha256(phone) : "",
    fn_hash: firstName ? sha256(firstName) : "",
    ln_hash: lastName ? sha256(lastName) : "",
    has_email: !!email,
    has_phone: !!phone
  };
}).filter(p => p.has_email || p.has_phone);

const withEmail = prepared.filter(p => p.has_email).length;
const withPhone = prepared.filter(p => p.has_phone).length;

return {
  hashed_users_json: JSON.stringify(prepared),
  total_prepared: prepared.length,
  with_email: withEmail,
  with_phone: withPhone,
  has_valid_data: prepared.length > 0 ? "yes" : "no",
  pii_fields_used: "email, phone, first_name, last_name",
  prep_summary: prepared.length + " users prepared (" + withEmail + " emails, " + withPhone + " phones)"
};`,
          outputVariables: [
            { name: "hashed_users_json", type: "string" },
            { name: "total_prepared", type: "number" },
            { name: "with_email", type: "number" },
            { name: "with_phone", type: "number" },
            { name: "has_valid_data", type: "string" },
            { name: "pii_fields_used", type: "string" },
            { name: "prep_summary", type: "string" },
          ],
        },
      }),

      // 3. PII compliance validator condition
      n("3", "condition", "Is data compliant for audience upload?", 520, {
        conditionData: [
          {
            variable: "has_valid_data",
            value: "yes",
            comparison_operator: "EQUAL",
            logical_operator: "AND",
          },
          {
            variable: "total_prepared",
            value: "20",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Meta Custom Audience upload
      n("4", "hitApi", "Upload audience to Meta Custom Audiences", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{meta_ad_account_id}/customaudiences",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "CRM Sync — High-Value Customers {timestamp}",
            },
            { key: "subtype", value: "CUSTOM" },
            { key: "customer_file_source", value: "USER_PROVIDED_ONLY" },
            {
              key: "description",
              value: "Auto-synced from HubSpot CRM. {prep_summary}",
            },
          ],
          variables: [
            { name: "id", customName: "meta_audience_id", type: "string" },
          ],
        },
      }),

      // 5. Audience match rate monitor
      n("5", "hitApi", "Check audience match rate", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/{meta_audience_id}?fields=name,approximate_count,operation_status,delivery_status",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "approximate_count",
              customName: "audience_matched_count",
              type: "number",
            },
            {
              name: "operation_status.status",
              customName: "audience_operation_status",
              type: "string",
            },
            {
              name: "delivery_status.status",
              customName: "audience_delivery_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Sync confirmation (Sheets)
      sheetsNode(
        "6",
        "Log audience sync to Sheets",
        1240,
        "AudienceSync",
        '[["{timestamp}","{meta_audience_id}","{total_prepared}","{with_email}","{with_phone}","{audience_matched_count}","{audience_operation_status}","{prep_summary}"]]',
      ),

      // 7. Sync notification (Slack)
      slackNode(
        "7",
        "Send audience sync notification to Slack",
        1480,
        ":busts_in_silhouette: *Meta Custom Audience Synced*\\n*Audience ID:* {meta_audience_id}\\n*Users Uploaded:* {total_prepared}\\n*Matched:* {audience_matched_count}\\n*Match Rate:* ~{audience_matched_count}/{total_prepared}\\n*Status:* {audience_operation_status}\\n\\n{prep_summary}\\n*PII Fields:* {pii_fields_used}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Compliant -> upload
      e("3", "7", "output_2"), // Not compliant -> notify only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1064. META EXCLUSION AUDIENCE SYNC (7 nodes)
  // ===================================================================

  "Meta Exclusion Audience Sync": {
    summary:
      "Aggregates exclusion lists from multiple sources, normalizes and hashes data in code, cross-references compliance rules via condition, uploads exclusion audiences to Meta, applies exclusions to active campaigns, estimates waste savings, and logs sync reports to Sheets and Slack.",
    nodes: [
      trigger("Schedule: Daily exclusion audience sync", "schedule", {
        schedule: "0 5 * * * (Daily at 5 AM)",
        howItStarts:
          "A daily schedule aggregates suppression and exclusion lists (existing customers, churned users, competitors, internal staff) and syncs them to Meta as exclusion audiences to prevent wasted ad spend",
        setup:
          "Configure HubSpot API key for customer and suppression lists. Provide Meta Marketing API access token and ad account ID. Define exclusion categories: existing customers, churned < 30 days, internal employees, competitors. Provide Google Sheets ID and Slack webhook.",
        inputFields: [],
      }),

      // 2. Data normalization + hashing (runCode)
      n("2", "runCode", "Normalize and hash exclusion data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate and normalize exclusion lists from multiple sources
const customers = JSON.parse(vars.existing_customers_json || "[]");
const churned = JSON.parse(vars.churned_users_json || "[]");
const internalStaff = JSON.parse(vars.internal_staff_json || "[]");

function sha256(str) {
  return "sha256_" + str.toLowerCase().trim().replace(/[^a-z0-9@.]/g, "");
}

const allExclusions = [];
const addList = (list, source) => {
  list.forEach(item => {
    const email = item.email || item.properties?.email || "";
    if (email) {
      allExclusions.push({ email_hash: sha256(email), source, original_email: email });
    }
  });
};

addList(customers, "existing_customer");
addList(churned, "churned_user");
addList(internalStaff, "internal_staff");

// Deduplicate by email hash
const seen = new Set();
const deduped = allExclusions.filter(e => {
  if (seen.has(e.email_hash)) return false;
  seen.add(e.email_hash);
  return true;
});

const bySource = {
  existing: deduped.filter(e => e.source === "existing_customer").length,
  churned: deduped.filter(e => e.source === "churned_user").length,
  internal: deduped.filter(e => e.source === "internal_staff").length
};

return {
  exclusion_hashes_json: JSON.stringify(deduped.map(e => ({ email: e.email_hash }))),
  total_exclusions: deduped.length,
  duplicates_removed: allExclusions.length - deduped.length,
  existing_customer_count: bySource.existing,
  churned_user_count: bySource.churned,
  internal_staff_count: bySource.internal,
  has_exclusions: deduped.length > 0 ? "yes" : "no",
  exclusion_summary: deduped.length + " unique exclusions (" + bySource.existing + " customers, " + bySource.churned + " churned, " + bySource.internal + " internal)"
};`,
          outputVariables: [
            { name: "exclusion_hashes_json", type: "string" },
            { name: "total_exclusions", type: "number" },
            { name: "duplicates_removed", type: "number" },
            { name: "existing_customer_count", type: "number" },
            { name: "churned_user_count", type: "number" },
            { name: "internal_staff_count", type: "number" },
            { name: "has_exclusions", type: "string" },
            { name: "exclusion_summary", type: "string" },
          ],
        },
      }),

      // 3. Compliance cross-reference condition
      n("3", "condition", "Are there valid exclusions to upload?", 520, {
        conditionData: [
          {
            variable: "has_exclusions",
            value: "yes",
            comparison_operator: "EQUAL",
            logical_operator: "AND",
          },
          {
            variable: "total_exclusions",
            value: "10",
            comparison_operator: "GREATER THAN",
            logical_operator: "END",
          },
        ],
      }),

      // 4. Meta exclusion audience upload
      n("4", "hitApi", "Upload exclusion audience to Meta", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{meta_ad_account_id}/customaudiences",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Exclusion — Combined Suppression List {timestamp}",
            },
            { key: "subtype", value: "CUSTOM" },
            { key: "customer_file_source", value: "USER_PROVIDED_ONLY" },
            {
              key: "description",
              value: "Auto-synced exclusion list. {exclusion_summary}",
            },
          ],
          variables: [
            { name: "id", customName: "exclusion_audience_id", type: "string" },
          ],
        },
      }),

      // 5. Campaign exclusion applier
      n("5", "hitApi", "Apply exclusion audience to active campaigns", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{active_adset_id}",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "targeting",
              value:
                '{"excluded_custom_audiences":[{"id":"{exclusion_audience_id}"}]}',
            },
          ],
          variables: [
            {
              name: "success",
              customName: "exclusion_applied",
              type: "boolean",
            },
          ],
        },
      }),

      // 6. Waste savings estimator (runCode)
      n("6", "runCode", "Estimate ad waste savings from exclusions", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Estimate ad spend saved by excluding these users
const totalExclusions = parseInt(vars.total_exclusions) || 0;
const avgCPM = 12.50; // Average Meta CPM in USD
const avgFrequency = 3.5; // Average times an excluded user would see ads per week
const avgImpressions = totalExclusions * avgFrequency;
const weeklyWasteSaved = (avgImpressions / 1000) * avgCPM;
const monthlyWasteSaved = weeklyWasteSaved * 4.33;
const annualWasteSaved = monthlyWasteSaved * 12;

return {
  estimated_weekly_savings: weeklyWasteSaved.toFixed(2),
  estimated_monthly_savings: monthlyWasteSaved.toFixed(2),
  estimated_annual_savings: annualWasteSaved.toFixed(2),
  wasted_impressions_prevented: avgImpressions,
  savings_summary: "$" + weeklyWasteSaved.toFixed(0) + "/week, $" + monthlyWasteSaved.toFixed(0) + "/month, $" + annualWasteSaved.toFixed(0) + "/year saved"
};`,
          outputVariables: [
            { name: "estimated_weekly_savings", type: "string" },
            { name: "estimated_monthly_savings", type: "string" },
            { name: "estimated_annual_savings", type: "string" },
            { name: "wasted_impressions_prevented", type: "number" },
            { name: "savings_summary", type: "string" },
          ],
        },
      }),

      // 7. Sync report (Sheets + Slack)
      sheetsNode(
        "7",
        "Log exclusion sync and savings to Sheets",
        1480,
        "ExclusionSync",
        '[["{timestamp}","{exclusion_audience_id}","{total_exclusions}","{existing_customer_count}","{churned_user_count}","{internal_staff_count}","{duplicates_removed}","{exclusion_applied}","{estimated_monthly_savings}","{savings_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Valid exclusions -> upload
      e("3", "7", "output_2"), // Insufficient data -> log only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1065. MILESTONE ACHIEVEMENT CELEBRATION (8 nodes)
  // ===================================================================

  "Milestone Achievement Celebration": {
    summary:
      "Triggers on milestone events, routes by milestone type via condition, looks up customer profiles from HubSpot, generates AI celebration content, sends celebration emails with rewards, dispatches SMS for VIPs via Twilio, invites social proof sharing, and logs milestones to Sheets.",
    nodes: [
      trigger("Webhook: Customer milestone achieved", "webhook_app", {
        source: "App / CRM",
        event: "milestone.achieved",
        howItStarts:
          "A customer reaches a milestone (anniversary, purchase count, loyalty tier upgrade, referral goal) which triggers a personalized celebration sequence with rewards",
        setup:
          "Configure your app or CRM to POST milestone events with customer identifier and milestone type. Provide HubSpot API key for profile enrichment. Set up Twilio for VIP SMS, email templates for celebrations, and Google Sheets ID for logging.",
        inputFields: [],
      }),

      // 2. Milestone type router condition
      n(
        "2",
        "condition",
        "Is this a VIP-tier milestone (anniversary or tier upgrade)?",
        280,
        {
          conditionData: [
            {
              variable: "milestone_type",
              value: "anniversary",
              comparison_operator: "EQUAL",
              logical_operator: "OR",
            },
            {
              variable: "milestone_type",
              value: "tier_upgrade",
              comparison_operator: "EQUAL",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 3. Customer profile lookup (HubSpot)
      hubspotGetNode(
        "3",
        "Fetch customer profile from HubSpot",
        520,
        "crm/v3/objects/contacts/{contact_id}?properties=email,firstname,lastname,phone,loyalty_tier,total_revenue,num_associated_deals,createdate",
        [
          {
            name: "properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "customer_firstname",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "customer_lastname",
            type: "string",
          },
          {
            name: "properties.phone",
            customName: "customer_phone",
            type: "string",
          },
          {
            name: "properties.loyalty_tier",
            customName: "customer_tier",
            type: "string",
          },
          {
            name: "properties.total_revenue",
            customName: "customer_ltv",
            type: "string",
          },
        ],
      ),

      // 4. AI celebration content
      aiNode(
        "4",
        "AI: Generate celebration content",
        760,
        "You are a customer delight specialist. Create genuinely celebratory content that makes customers feel valued and special. Include specific milestone details and personalized rewards. Return JSON.",
        "Milestone achieved — Type: {milestone_type}, Customer: {customer_firstname} {customer_lastname}, Tier: {customer_tier}, LTV: ${customer_ltv}. Generate celebration_subject (joyful, specific to milestone), celebration_body (HTML with confetti-themed design, personalized congratulations, specific reward details, social sharing CTA), sms_celebration (under 160 chars, warm and personal), reward_code (unique-looking alphanumeric), reward_description (what they earn), social_share_message (pre-written share text for customer to post).",
        [
          {
            field: "celebration_subject",
            as: "celebration_subject",
            type: "string",
          },
          {
            field: "celebration_body",
            as: "celebration_body_html",
            type: "string",
          },
          { field: "sms_celebration", as: "celebration_sms", type: "string" },
          { field: "reward_code", as: "reward_code", type: "string" },
          {
            field: "reward_description",
            as: "reward_description",
            type: "string",
          },
          {
            field: "social_share_message",
            as: "social_share_text",
            type: "string",
          },
        ],
      ),

      // 5. Celebration email with reward
      emailNode(
        "5",
        "Send celebration email with reward",
        1000,
        "{customer_email}",
        "{celebration_subject}",
        "{celebration_body_html}",
      ),

      // 6. SMS for VIP (Twilio)
      smsNode(
        "6",
        "Send VIP celebration SMS via Twilio",
        1240,
        "{customer_phone}",
        "{celebration_sms}",
      ),

      // 7. Social proof invitation
      emailNode(
        "7",
        "Send social sharing invitation",
        1480,
        "{customer_email}",
        "Share your achievement with the world!",
        'Hi {customer_firstname},\\n\\nWe are so proud of your milestone! Would you like to share it?\\n\\nHere is a pre-written post you can customize:\\n\\n\\"{social_share_text}\\"\\n\\nYour reward code {reward_code} is already active: {reward_description}\\n\\nThank you for being an amazing customer!',
      ),

      // 8. Milestone logger (Sheets)
      sheetsNode(
        "8",
        "Log milestone achievement to Sheets",
        1720,
        "Milestones",
        '[["{timestamp}","{customer_email}","{customer_firstname} {customer_lastname}","{milestone_type}","{customer_tier}","{customer_ltv}","{reward_code}","{reward_description}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // VIP milestone -> full celebration with SMS
      e("2", "3", "output_2"), // Standard milestone -> still celebrate but no SMS
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1066. MISSING CONSENT-SOURCE REMEDIATION (8 nodes)
  // ===================================================================

  "Missing Consent-Source Remediation": {
    summary:
      "Weekly consent audit identifies records missing consent source, classifies risk severity in code, limits batch size for processing, generates AI-crafted re-consent emails, dispatches re-consent requests, handles consent responses, suppresses non-responders in code, and logs everything to a compliance audit sheet.",
    nodes: [
      trigger("Schedule: Weekly consent audit", "schedule", {
        schedule: "0 6 * * 1 (Monday at 6 AM)",
        howItStarts:
          "A weekly schedule audits the contact database for records missing proper consent source documentation, triggering remediation workflows to maintain GDPR/CCPA compliance",
        setup:
          "Configure HubSpot API key with contacts read/write access. Define consent source requirements per regulation (GDPR: explicit opt-in timestamp + source; CCPA: right to opt-out documented). Provide Google Sheets ID for audit logging and email sending credentials.",
        inputFields: [],
      }),

      // 2. Risk severity classifier (runCode)
      n("2", "runCode", "Classify consent risk severity", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Audit contacts for missing consent sources and classify risk
const contacts = JSON.parse(vars.audit_contacts_json || "[]");
const now = Date.now();
const dayMs = 86400000;

const atRisk = contacts.map(c => {
  const email = c.properties?.email || "";
  const consentSource = c.properties?.consent_source || "";
  const consentDate = c.properties?.consent_date || "";
  const region = c.properties?.ip_country || c.properties?.country || "";
  const createDate = c.properties?.createdate || "";
  const daysSinceCreated = createDate ? Math.floor((now - new Date(createDate).getTime()) / dayMs) : 999;

  let severity = "low";
  const isEU = ["DE","FR","GB","IT","ES","NL","BE","AT","SE","PL","DK","FI","IE","PT","CZ","RO","HU","GR","BG","HR","SK","SI","LT","LV","EE","LU","MT","CY"].includes(region);
  const isCalifornia = region === "CA" || region === "US-CA";

  if (!consentSource && (isEU || isCalifornia)) severity = "critical";
  else if (!consentSource && daysSinceCreated > 180) severity = "high";
  else if (!consentSource) severity = "medium";
  else severity = "compliant";

  return { email, consentSource, consentDate, region, severity, daysSinceCreated, contact_id: c.id };
}).filter(c => c.severity !== "compliant");

const critical = atRisk.filter(c => c.severity === "critical");
const high = atRisk.filter(c => c.severity === "high");
const medium = atRisk.filter(c => c.severity === "medium");

return {
  at_risk_contacts_json: JSON.stringify(atRisk),
  total_at_risk: atRisk.length,
  critical_count: critical.length,
  high_count: high.length,
  medium_count: medium.length,
  has_critical: critical.length > 0 ? "yes" : "no",
  risk_summary: critical.length + " critical, " + high.length + " high, " + medium.length + " medium risk records",
  sample_email: atRisk.length > 0 ? atRisk[0].email : ""
};`,
          outputVariables: [
            { name: "at_risk_contacts_json", type: "string" },
            { name: "total_at_risk", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "high_count", type: "number" },
            { name: "medium_count", type: "number" },
            { name: "has_critical", type: "string" },
            { name: "risk_summary", type: "string" },
            { name: "sample_email", type: "string" },
          ],
        },
      }),

      // 3. Batch size limiter (runCode)
      n("3", "runCode", "Limit batch size for safe processing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Limit batch size to prevent overwhelming email systems
const contacts = JSON.parse(vars.at_risk_contacts_json || "[]");
const maxBatchSize = 200;
const prioritized = contacts
  .sort((a, b) => {
    const severityOrder = { critical: 0, high: 1, medium: 2 };
    return (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
  })
  .slice(0, maxBatchSize);

const remaining = contacts.length - prioritized.length;

return {
  batch_contacts_json: JSON.stringify(prioritized),
  batch_size: prioritized.length,
  remaining_contacts: remaining,
  batch_limited: contacts.length > maxBatchSize ? "yes" : "no",
  batch_summary: "Processing " + prioritized.length + " of " + contacts.length + " (" + remaining + " deferred to next cycle)"
};`,
          outputVariables: [
            { name: "batch_contacts_json", type: "string" },
            { name: "batch_size", type: "number" },
            { name: "remaining_contacts", type: "number" },
            { name: "batch_limited", type: "string" },
            { name: "batch_summary", type: "string" },
          ],
        },
      }),

      // 4. AI re-consent email
      aiNode(
        "4",
        "AI: Generate re-consent request email",
        760,
        "You are a compliance communications specialist. Write a clear, non-threatening re-consent email that explains why consent is needed, makes it easy to respond, and complies with GDPR/CCPA language requirements. Return JSON.",
        "Consent remediation needed for {batch_size} contacts. Risk breakdown: {risk_summary}. Generate reconsent_subject (transparent, non-alarming), reconsent_body (HTML with clear explanation of why re-consent is needed, easy one-click consent button, option to unsubscribe, legal footer with data processing explanation), reconsent_plain_text (accessible fallback).",
        [
          {
            field: "reconsent_subject",
            as: "reconsent_subject",
            type: "string",
          },
          {
            field: "reconsent_body",
            as: "reconsent_body_html",
            type: "string",
          },
          {
            field: "reconsent_plain_text",
            as: "reconsent_plain",
            type: "string",
          },
        ],
      ),

      // 5. Re-consent email dispatch
      emailNode(
        "5",
        "Dispatch re-consent request emails",
        1000,
        "{sample_email}",
        "{reconsent_subject}",
        "{reconsent_body_html}",
      ),

      // 6. Consent response handler (hitApi — check for responses)
      n("6", "hitApi", "Check for consent responses via webhook log", 1240, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.hubapi.com/crm/v3/objects/contacts?properties=email,consent_source,consent_date&filterGroups=[{"filters":[{"propertyName":"consent_date","operator":"GTE","value":"{one_week_ago_ms}"}]}]&limit=100',
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results",
              customName: "consent_responses_json",
              type: "string",
            },
            {
              name: "total",
              customName: "consent_response_count",
              type: "number",
            },
          ],
        },
      }),

      // 7. Non-responder suppression (runCode)
      n("7", "runCode", "Identify and suppress non-responders", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare sent batch against responses to identify non-responders
const batch = JSON.parse(vars.batch_contacts_json || "[]");
const responses = JSON.parse(vars.consent_responses_json || "[]");
const respondedEmails = new Set(responses.map(r => r.properties?.email).filter(Boolean));

const nonResponders = batch.filter(c => !respondedEmails.has(c.email));
const responded = batch.filter(c => respondedEmails.has(c.email));

// Non-responders with critical severity should be suppressed
const toSuppress = nonResponders.filter(c => c.severity === "critical");
const toRetry = nonResponders.filter(c => c.severity !== "critical");

return {
  responded_count: responded.length,
  non_responder_count: nonResponders.length,
  suppress_count: toSuppress.length,
  retry_count: toRetry.length,
  response_rate: batch.length > 0 ? ((responded.length / batch.length) * 100).toFixed(1) : "0",
  suppress_emails_json: JSON.stringify(toSuppress.map(c => c.email)),
  compliance_summary: responded.length + " consented, " + toSuppress.length + " suppressed, " + toRetry.length + " queued for retry"
};`,
          outputVariables: [
            { name: "responded_count", type: "number" },
            { name: "non_responder_count", type: "number" },
            { name: "suppress_count", type: "number" },
            { name: "retry_count", type: "number" },
            { name: "response_rate", type: "string" },
            { name: "suppress_emails_json", type: "string" },
            { name: "compliance_summary", type: "string" },
          ],
        },
      }),

      // 8. Compliance audit log (Sheets)
      sheetsNode(
        "8",
        "Log consent audit to compliance Sheets",
        1720,
        "ConsentAudit",
        '[["{timestamp}","{total_at_risk}","{risk_summary}","{batch_size}","{responded_count}","{non_responder_count}","{suppress_count}","{response_rate}%","{compliance_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1067. MULTI-CREATOR VARIANT PIPELINE (9 nodes)
  // ===================================================================

  "Multi-Creator Variant Pipeline": {
    summary:
      "Accepts a creative brief, selects creator personas in code, generates AI multi-variant scripts for different styles, scores script quality, selects top variants via condition, submits videos for rendering, routes through an approval gate, publishes approved videos via Buffer, and tracks pipeline performance in Sheets.",
    nodes: [
      trigger("Webhook: Creative brief submitted", "webhook_app", {
        source: "Project Management Tool",
        event: "brief.submitted",
        howItStarts:
          "A creative brief is submitted for video content production, triggering a multi-variant pipeline that generates scripts in different creator styles, scores them, and routes winners through approval and publishing",
        setup:
          "Configure webhook to receive briefs with fields: topic, target_audience, platform, tone, key_messages. Provide OpenAI API key for multi-variant generation. Set up video rendering API credentials, Buffer API key for publishing, and Google Sheets ID for tracking.",
        inputFields: [],
      }),

      // 2. Creator persona selector (runCode)
      n("2", "runCode", "Select creator personas for variants", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select creator personas based on brief requirements
const payload = vars.webhook_payload || "{}";
const brief = typeof payload === "string" ? JSON.parse(payload) : payload;
const topic = brief.topic || brief.subject || "";
const audience = brief.target_audience || brief.audience || "";
const platform = brief.platform || "instagram";
const tone = brief.tone || "engaging";
const keyMessages = brief.key_messages || brief.messages || "";

const personas = [
  { id: "educator", name: "The Educator", style: "Clear, authoritative, uses analogies and data. Speaks like a trusted professor.", best_for: ["explainer", "tutorial", "thought-leadership"] },
  { id: "storyteller", name: "The Storyteller", style: "Narrative-driven, emotional hooks, personal anecdotes. Builds tension and resolution.", best_for: ["brand", "testimonial", "case-study"] },
  { id: "entertainer", name: "The Entertainer", style: "High energy, humor, trend-aware, meme-literate. Fast-paced and scroll-stopping.", best_for: ["social", "viral", "awareness"] },
  { id: "minimalist", name: "The Minimalist", style: "Clean, concise, premium feel. Says more with less. Elegant and sophisticated.", best_for: ["luxury", "product", "premium"] },
  { id: "provocateur", name: "The Provocateur", style: "Bold, contrarian, challenges conventions. Strong opinions backed by evidence.", best_for: ["debate", "disruption", "challenger-brand"] }
];

// Select top 3 most relevant personas
const topicLower = topic.toLowerCase() + " " + audience.toLowerCase() + " " + tone.toLowerCase();
const scored = personas.map(p => {
  let score = 0;
  p.best_for.forEach(b => { if (topicLower.includes(b)) score += 10; });
  if (topicLower.includes(p.id)) score += 15;
  return { ...p, relevance_score: score };
}).sort((a, b) => b.relevance_score - a.relevance_score);

const selected = scored.slice(0, 3);

return {
  selected_personas_json: JSON.stringify(selected),
  persona_count: selected.length,
  persona_names: selected.map(p => p.name).join(", "),
  brief_topic: topic,
  brief_audience: audience,
  brief_platform: platform,
  brief_tone: tone,
  brief_key_messages: keyMessages,
  persona_summary: selected.length + " personas: " + selected.map(p => p.name).join(", ")
};`,
          outputVariables: [
            { name: "selected_personas_json", type: "string" },
            { name: "persona_count", type: "number" },
            { name: "persona_names", type: "string" },
            { name: "brief_topic", type: "string" },
            { name: "brief_audience", type: "string" },
            { name: "brief_platform", type: "string" },
            { name: "brief_tone", type: "string" },
            { name: "brief_key_messages", type: "string" },
            { name: "persona_summary", type: "string" },
          ],
        },
      }),

      // 3. AI multi-variant script generator
      aiNode(
        "3",
        "AI: Generate multi-variant video scripts",
        520,
        "You are a video content strategist who can write in multiple creator styles. For each persona provided, write a complete video script that captures that persona unique voice while conveying the same key messages. Return JSON.",
        "Brief — Topic: {brief_topic}, Audience: {brief_audience}, Platform: {brief_platform}, Tone: {brief_tone}, Key Messages: {brief_key_messages}. Creator personas: {selected_personas_json}. For each persona, generate a video script. Return variant_1_title, variant_1_script (full script with [SCENE] directions, dialogue, and timing notes), variant_1_persona (persona name), variant_2_title, variant_2_script, variant_2_persona, variant_3_title, variant_3_script, variant_3_persona, estimated_production_time (hours for all 3).",
        [
          { field: "variant_1_title", as: "v1_title", type: "string" },
          { field: "variant_1_script", as: "v1_script", type: "string" },
          { field: "variant_1_persona", as: "v1_persona", type: "string" },
          { field: "variant_2_title", as: "v2_title", type: "string" },
          { field: "variant_2_script", as: "v2_script", type: "string" },
          { field: "variant_2_persona", as: "v2_persona", type: "string" },
          { field: "variant_3_title", as: "v3_title", type: "string" },
          { field: "variant_3_script", as: "v3_script", type: "string" },
          { field: "variant_3_persona", as: "v3_persona", type: "string" },
          {
            field: "estimated_production_time",
            as: "est_production_hours",
            type: "string",
          },
        ],
      ),

      // 4. Script quality scorer (runCode)
      n("4", "runCode", "Score script quality across variants", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score each variant on key quality dimensions
function scoreScript(title, script, persona) {
  let score = 0;
  const wordCount = (script || "").split(/\\s+/).length;
  const hasSceneDirections = (script || "").includes("[SCENE]");
  const hasCTA = (script || "").toLowerCase().includes("call to action") || (script || "").toLowerCase().includes("cta") || (script || "").toLowerCase().includes("subscribe") || (script || "").toLowerCase().includes("click");
  const hasHook = (script || "").toLowerCase().includes("hook") || wordCount > 10; // Opening words matter
  const keyMessages = vars.brief_key_messages || "";
  const coversMessages = keyMessages.split(",").filter(m => (script || "").toLowerCase().includes(m.trim().toLowerCase())).length;

  // Scoring rubric (out of 100)
  if (wordCount >= 100 && wordCount <= 500) score += 25; // Ideal length
  else if (wordCount >= 50) score += 15;
  if (hasSceneDirections) score += 15; // Professional formatting
  if (hasCTA) score += 20; // Has call to action
  if (hasHook) score += 15; // Opens with a hook
  score += Math.min(coversMessages * 5, 25); // Key message coverage

  return { title, persona, word_count: wordCount, score, has_cta: hasCTA, has_scene_dirs: hasSceneDirections };
}

const v1 = scoreScript(vars.v1_title, vars.v1_script, vars.v1_persona);
const v2 = scoreScript(vars.v2_title, vars.v2_script, vars.v2_persona);
const v3 = scoreScript(vars.v3_title, vars.v3_script, vars.v3_persona);

const variants = [v1, v2, v3].sort((a, b) => b.score - a.score);
const winner = variants[0];
const hasHighQuality = winner.score >= 60;

return {
  scored_variants_json: JSON.stringify(variants),
  winner_title: winner.title,
  winner_persona: winner.persona,
  winner_score: winner.score,
  v1_score: v1.score,
  v2_score: v2.score,
  v3_score: v3.score,
  has_quality_winner: hasHighQuality ? "yes" : "no",
  scoring_summary: "Winner: " + winner.title + " (" + winner.persona + ") with score " + winner.score + "/100"
};`,
          outputVariables: [
            { name: "scored_variants_json", type: "string" },
            { name: "winner_title", type: "string" },
            { name: "winner_persona", type: "string" },
            { name: "winner_score", type: "number" },
            { name: "v1_score", type: "number" },
            { name: "v2_score", type: "number" },
            { name: "v3_score", type: "number" },
            { name: "has_quality_winner", type: "string" },
            { name: "scoring_summary", type: "string" },
          ],
        },
      }),

      // 5. Top variant selector condition
      n(
        "5",
        "condition",
        "Does the winning variant meet quality threshold (score >= 60)?",
        1000,
        {
          conditionData: [
            {
              variable: "has_quality_winner",
              value: "yes",
              comparison_operator: "EQUAL",
              logical_operator: "END",
            },
          ],
        },
      ),

      // 6. Video rendering submission
      n("6", "hitApi", "Submit winning script for video rendering", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.synthesia.io/v2/videos",
          headers: [
            { key: "Authorization", value: "Bearer {synthesia_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{winner_title}" },
            {
              key: "description",
              value:
                "Auto-generated from {winner_persona} persona. Score: {winner_score}/100.",
            },
            {
              key: "input",
              value:
                '[{"scriptText":"{v1_script}","avatar":"{synthesia_avatar_id}","background":"{synthesia_background}"}]',
            },
            { key: "test", value: "false" },
          ],
          variables: [
            { name: "id", customName: "render_job_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
          ],
        },
      }),

      // 7. Approval routing (approval gate)
      n("7", "approvalGate", "Review and approve rendered video", 1480, {
        approvalGateDetails: {
          approvers: ["{creative_director_email}"],
          message:
            "Video ready for review — Title: {winner_title}. Persona: {winner_persona}. Quality score: {winner_score}/100. Render ID: {render_job_id}. Platform: {brief_platform}. Please review and approve for publishing.",
          timeoutHours: 48,
        },
      }),

      // 8. Approved video publisher (Buffer)
      bufferNode(
        "8",
        "Publish approved video via Buffer",
        1720,
        "New video: {winner_title}\\n\\n{brief_key_messages}\\n\\n#content #{brief_platform} #{brief_topic}",
      ),

      // 9. Pipeline performance tracker (Sheets)
      sheetsNode(
        "9",
        "Log pipeline performance to Sheets",
        1960,
        "CreatorPipeline",
        '[["{timestamp}","{brief_topic}","{brief_platform}","{persona_names}","{v1_score}","{v2_score}","{v3_score}","{winner_title}","{winner_persona}","{winner_score}","{render_job_id}","{render_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Quality winner -> render video
      e("5", "9", "output_2"), // Below quality threshold -> log and skip
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
